import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
	CalendarRange,
	ChevronDown,
	ChevronUp,
	Download,
	FileUp,
	Info,
	Plus,
	Search,
	Store,
	Trash2,
	X,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSidebarV2 } from "@/components/sidebar/sidebar-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
	attemptDynamicImportRecovery,
	describeDynamicImportError,
	loadXlsx,
} from "@/lib/dynamic-import";
import { selectNewProductsForBasket } from "./basket-products";
import {
	type ImportReconciliation,
	ImportReportPanel,
	KSID_FORMAT,
	reconcileCodeImport,
} from "./code-import-report";
import {
	DEFAULT_CONTRACT_PERIOD,
	getContractDateRange,
	isValidPeriodCode,
	normalizeContractYear,
	PERIOD_OPTIONS,
} from "./contract-period";
import {
	type BasketDetail,
	type ContractBrandDetail,
	type ContractBrandSettlementTierDetail,
	type ContractMslItemDetail,
	type ContractOwnerCandidate,
	type ContractProductSurchargeDetail,
	type CreateContractRequest,
	contractsApi,
	type PharmacyItem,
	type ProductBrief,
	type ProductItem,
	type SettlementDetail,
	type SettlementTierDetail,
} from "./contracts-api";
import { NEW_CONTRACT_QUERY_KEY } from "./contracts-query-keys";
import { ExFactoryPriceListsSection } from "./exfactory-price-lists-section";
import { PharmacySelectorModal } from "./pharmacy-selector-modal";
import { ProducerSelector } from "./producer-selector";
import { ProductSelectorModal } from "./product-selector-modal";

interface ContractPharmacyWithDates extends PharmacyItem {
	startDate: string;
	endDate: string;
}

export interface SettlementTierWithTempId extends SettlementTierDetail {
	tempId: string;
}

export interface SettlementWithTempId extends Omit<SettlementDetail, "tiers"> {
	tempId: string;
	tiers: SettlementTierWithTempId[];
}

interface BasketProductWithBrand extends ProductBrief {
	brandClientId?: string;
}

export interface BasketWithTempId extends Omit<BasketDetail, "settlements" | "products"> {
	tempId: string;
	products: BasketProductWithBrand[];
	settlements: SettlementWithTempId[];
}

interface ProductSurchargeWithTempId extends ContractProductSurchargeDetail {
	tempId: string;
}

interface MslItemWithTempId extends ContractMslItemDetail {
	tempId: string;
}

interface ContractBrandWithTempId extends Omit<ContractBrandDetail, "settlementTiers"> {
	tempId: string;
	settlementTiers: ContractBrandSettlementTierWithTempId[];
}

interface ContractBrandSettlementTierWithTempId extends ContractBrandSettlementTierDetail {
	tempId: string;
}

interface ParsedBrandSettlementTierImport {
	thresholdPercent: number;
	settlementPercent?: number;
	settlementAmount?: number;
}

type SettlementComponentFieldKey =
	| "marketingFixedAmount"
	| "marketingFixedPercent"
	| "marketingPurchaseAmount"
	| "marketingSalesAmount"
	| "marketingSalesPercent"
	| "marketingPurchasePercent"
	| "exhibitionFixedAmount"
	| "exhibitionFixedPercent"
	| "exhibitionPurchaseAmount"
	| "exhibitionPurchasePercent"
	| "exhibitionSalesPercent"
	| "mslFixedAmount"
	| "mslFixedPercent"
	| "mslPurchaseAmount"
	| "mslPurchasePercent"
	| "mslSalesAmount"
	| "mslSalesPercent"
	| "reportingPurchaseAmount"
	| "reportingPurchasePercent"
	| "reportingSalesPercent"
	| "reportingSalesAmount";

const settlementComponentFields: Array<{ key: SettlementComponentFieldKey; label: string }> = [
	{ key: "marketingFixedAmount", label: "Marketing stały (zł)" },
	{ key: "marketingFixedPercent", label: "Marketing stały (%)" },
	{ key: "marketingPurchaseAmount", label: "Marketing naliczany od zakupu (zł)" },
	{ key: "marketingSalesAmount", label: "Marketing naliczany od sprzedaży (zł)" },
	{ key: "marketingSalesPercent", label: "Marketing naliczany od sprzedaży (%)" },
	{ key: "marketingPurchasePercent", label: "Marketing naliczany od zakupu (%)" },
	{ key: "exhibitionFixedAmount", label: "Ekspozycja (zł)" },
	{ key: "exhibitionFixedPercent", label: "Ekspozycja (%)" },
	{ key: "exhibitionPurchaseAmount", label: "Ekspozycja naliczana od zakupu (zł)" },
	{ key: "exhibitionPurchasePercent", label: "Ekspozycja naliczana od zakupu (%)" },
	{ key: "exhibitionSalesPercent", label: "Ekspozycja naliczana od sprzedaży (%)" },
	{ key: "mslFixedAmount", label: "MSL stały (zł)" },
	{ key: "mslFixedPercent", label: "MSL stały (%)" },
	{ key: "mslPurchaseAmount", label: "MSL naliczany od zakupu (zł)" },
	{ key: "mslPurchasePercent", label: "MSL naliczany od zakupu (%)" },
	{ key: "mslSalesAmount", label: "MSL naliczany od sprzedaży (zł)" },
	{ key: "mslSalesPercent", label: "MSL naliczany od sprzedaży (%)" },
	{ key: "reportingPurchaseAmount", label: "Raportowanie od zakupu (zł)" },
	{ key: "reportingPurchasePercent", label: "Raportowanie od zakupu (%)" },
	{ key: "reportingSalesPercent", label: "Raportowanie od sprzedaży (%)" },
	{ key: "reportingSalesAmount", label: "Raportowanie od sprzedaży (zł)" },
];

export function parseOptionalNumber(raw: string): number | undefined {
	if (raw.trim() === "") return undefined;
	// Usun biale znaki (spacja,  ) — separatory tysiecy ze sformatowanych
	// komorek xlsx (raw:false), np. "133 847,894" -> "133847.894".
	const cleaned = raw.replace(/\s/g, "").replace(",", ".");
	const value = Number(cleaned);
	return Number.isFinite(value) ? value : undefined;
}

/** Baza numeracji dat Excela (serial 1 = 1900-01-01 przy bugu roku 1900). */
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
/** Okno akceptowanych numerow seryjnych: 1990-01-01 .. 2099-12-31. Poza nim
 *  liczba to nie data, tylko wartosc z innej kolumny (np. KSID 895755). */
const EXCEL_SERIAL_MIN = 32874;
const EXCEL_SERIAL_MAX = 73050;

function pad2(value: number): string {
	return String(value).padStart(2, "0");
}

/**
 * Skleja ISO tylko dla daty, ktora naprawde istnieje w kalendarzu. Bez tej
 * kontroli plik z datami w formacie amerykanskim (01/15/2026) dawal
 * "2026-15-01": napis przechodzil walidacje ISO przed zapisem, ale
 * `<input type="date">` odmawial go wyswietlic, wiec pole wygladalo na puste.
 */
function buildIsoDate(year: number, month: number, day: number): string | undefined {
	if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
		return undefined;
	}
	const date = new Date(Date.UTC(year, month - 1, day));
	if (
		date.getUTCFullYear() !== year ||
		date.getUTCMonth() !== month - 1 ||
		date.getUTCDate() !== day
	) {
		return undefined;
	}
	return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Data z komorki importu -> ISO yyyy-mm-dd, albo `undefined` gdy wartosci nie
 * da sie odczytac jako daty. `undefined` jest tu wynikiem uzytecznym: wywolanie
 * podstawia wtedy zakres okresu kontraktu, wiec kolumny "Od"/"Do" moga w pliku
 * w ogole nie wystapic. Czytamy ISO, zapis dd.mm.rrrr (takze bez wiodacych zer,
 * z kropka lub ukosnikiem) oraz numer seryjny Excela — komorka sformatowana
 * jako data wraca z `sheet_to_json({raw: true})` wlasnie jako liczba.
 */
export function parseImportDate(value: unknown): string | undefined {
	const raw = String(value ?? "").trim();
	if (!raw) return undefined;

	const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
	if (iso) return buildIsoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

	const dmy = raw.replace(/[./]/g, "-").match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
	if (dmy) return buildIsoDate(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));

	const serial = Number(raw);
	if (Number.isInteger(serial) && serial >= EXCEL_SERIAL_MIN && serial <= EXCEL_SERIAL_MAX) {
		const date = new Date(EXCEL_EPOCH_UTC + serial * 86_400_000);
		return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
	}

	return undefined;
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Waliduje zakres z hurtowej zmiany dat aptek. Zwraca komunikat dla uzytkownika
 * albo `null`, gdy zakres jest do przyjecia. Wydzielone z handlera, bo to
 * jedyne miejsce, gdzie da sie recznie wprowadzic date konca wczesniejsza niz
 * poczatek — a backend przyjmuje `end_date` jako `NaiveDate` i odbija taki
 * zapis jako 422 bez wskazania apteki.
 */
export function validatePharmacyDateRange(startDate: string, endDate: string): string | null {
	if (!ISO_DATE_PATTERN.test(startDate)) return 'Uzupełnij datę "Od" przed zastosowaniem.';
	if (!ISO_DATE_PATTERN.test(endDate)) return 'Uzupełnij datę "Do" przed zastosowaniem.';
	if (endDate < startDate) return 'Data "Do" nie może być wcześniejsza niż "Od".';
	return null;
}

/**
 * Podmienia daty w aptekach o podanych KSID, reszte zostawia bez zmian.
 * Kolejnosc wierszy jest zachowana — po hurtowej zmianie tabela nie ma prawa
 * sie przetasowac, bo uzytkownik traci miejsce, w ktorym byl.
 */
export function setPharmacyDatesFor<T extends { unitcode: string }>(
	rows: T[],
	unitcodes: ReadonlySet<string>,
	range: { startDate: string; endDate: string },
): T[] {
	return rows.map((row) =>
		unitcodes.has(row.unitcode)
			? { ...row, startDate: range.startDate, endDate: range.endDate }
			: row,
	);
}

// Klucz porownania brandow, nie zapisywana nazwa. SheetJS 0.20 czyta z XLSX LF tam,
// gdzie brand zapisany wczesniej (albo z pliku .xls) ma CRLF.
export function normalizeBrandName(value: string): string {
	return value.replace(/\r\n?/g, "\n").trim().toLowerCase();
}

function normalizeImportHeader(value: unknown): string {
	return String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, " ");
}

function parseThresholdHeader(value: unknown): number | null {
	const normalized = normalizeImportHeader(value);
	const match = normalized.match(/^(\d+(?:[.,]\d+)?)\s*%$/);
	if (!match) return null;
	return Number(match[1].replace(",", "."));
}

export function parseBrandTierImportCell(
	value: unknown,
	thresholdPercent: number,
	rowNumber: number,
): ParsedBrandSettlementTierImport | null {
	const raw = String(value ?? "").trim();
	if (!raw) return null;

	const normalized = raw.toLowerCase().replace(/\s+/g, " ");
	const hasPercent = normalized.includes("%");
	const hasCurrency = normalized.includes("zł") || normalized.includes("zl");

	if (hasPercent && hasCurrency) {
		throw new Error(
			`Wiersz ${rowNumber}: próg ${thresholdPercent}% musi zawierać tylko jedną wartość: procent albo zł.`,
		);
	}

	if (!hasPercent && !hasCurrency) {
		throw new Error(
			`Wiersz ${rowNumber}: próg ${thresholdPercent}% musi mieć jednostkę % albo zł.`,
		);
	}

	const parsed = parseOptionalNumber(raw.replace(/%|zł|zl/gi, "").trim());
	if (parsed == null) {
		throw new Error(
			`Wiersz ${rowNumber}: nie udało się odczytać wartości dla progu ${thresholdPercent}%.`,
		);
	}

	if (hasPercent) {
		return { thresholdPercent, settlementPercent: parsed };
	}

	return { thresholdPercent, settlementAmount: parsed };
}

// Sentry FARMASTER-FRONTEND-3C: statyczne segmenty (np. "raporty") wpadaly w
// trase $id i Number(id)=NaN szlo do backendu jako /contracts/NaN (HTTP 400).
export function isValidContractId(id: string | undefined): boolean {
	return id !== undefined && /^\d+$/.test(id);
}

export function ContractFormPage() {
	const { id } = useParams({ strict: false });
	const isEdit = isValidContractId(id);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const formId = useId();
	const { isPanelOpen } = useSidebarV2();

	const [name, setName] = useState("");
	const [producerId, setProducerId] = useState<number | null>(null);
	// Pole "Rok" trzyma SUROWY tekst, a `year` jest pochodna — inaczej stan
	// przyjmowal `Number("")`/NaN w trakcie pisania i zakres okresu schodzil
	// do "NaN-NaN-NaN" (puste "Od"/"Do" przy imporcie aptek, 2026-08-13).
	const [yearInput, setYearInput] = useState(() => String(new Date().getFullYear()));
	const year = useMemo(() => normalizeContractYear(yearInput), [yearInput]);
	const [quarter, setQuarter] = useState(DEFAULT_CONTRACT_PERIOD);
	const [pricingMode, setPricingMode] = useState<"PURCHASE" | "EXFACTORY">("PURCHASE");
	const [ownerUserId, setOwnerUserId] = useState<number | null>(null);
	const [minTotalSalesValue, setMinTotalSalesValue] = useState<string>("");
	const [minTotalPurchaseValue, setMinTotalPurchaseValue] = useState<string>("");
	const [minTotalSalesQuantity, setMinTotalSalesQuantity] = useState<string>("");
	const [minTotalPurchaseQuantity, setMinTotalPurchaseQuantity] = useState<string>("");
	const [marketingGuaranteed, setMarketingGuaranteed] = useState<string>("");
	// Decyzja producenta o realizacji ekspozycji (MERCH), po zakonczeniu kontraktu.
	// "" = brak decyzji (NULL), "true"/"false" = Tak/Nie.
	const [exposureRealized, setExposureRealized] = useState<string>("");
	const [exposureDecidedAt, setExposureDecidedAt] = useState<string>("");
	const [exposureNote, setExposureNote] = useState<string>("");
	const [sumSalesPercentsOnPurchase, setSumSalesPercentsOnPurchase] = useState(false);
	const [contractTiers, setContractTiers] = useState<SettlementTierWithTempId[]>([]);
	const [pharmacies, setPharmacies] = useState<ContractPharmacyWithDates[]>([]);
	// Podsumowanie ostatniego importu aptek z pliku — toast znika, a użytkownik
	// musi móc odczytać i skopiować KSID, które nie weszły do kontraktu.
	const [pharmacyImportReport, setPharmacyImportReport] = useState<ImportReconciliation | null>(
		null,
	);
	const [brands, setBrands] = useState<ContractBrandWithTempId[]>([]);
	const [productSurcharges, setProductSurcharges] = useState<ProductSurchargeWithTempId[]>([]);
	const [baskets, setBaskets] = useState<BasketWithTempId[]>([]);
	// Tryb tworzenia: wybor cennikow exfactory trzymamy lokalnie do submita.
	// W trybie edycji komponent sam zapisuje przez osobny endpoint, ale rejestruje
	// pending save tutaj — handleSubmit wywola go razem z zapisem kontraktu.
	const [createExfactoryIds, setCreateExfactoryIds] = useState<number[]>([]);
	const pendingExfactorySaveRef = useRef<(() => Promise<void>) | null>(null);
	const [mslItems, setMslItems] = useState<MslItemWithTempId[]>([]);
	const [mslWymagalnoscTryb, setMslWymagalnoscTryb] = useState<"per_produkt" | "total">(
		"per_produkt",
	);
	const [mslProcentWymagalnosciGlobal, setMslProcentWymagalnosciGlobal] = useState("");

	const [isPharmaciesExpanded, setIsPharmaciesExpanded] = useState(false);
	const [pharmacySearchQuery, setPharmacySearchQuery] = useState("");
	const [basketProductSearches, setBasketProductSearches] = useState<Record<string, string>>({});
	// Cel dialogu "Usun wszystkie z koszyka" — trzymamy indeks i nazwe, zeby
	// tresc potwierdzenia mowila O KTORY koszyk chodzi (kontrakt ma ich kilka).
	const [clearProductsTarget, setClearProductsTarget] = useState<{
		basketIndex: number;
		name: string;
		count: number;
		visibleCount: number;
	} | null>(null);

	const [isProductModalOpen, setIsProductModalOpen] = useState(false);
	const [productSelectionTarget, setProductSelectionTarget] = useState<
		{ kind: "basket"; basketIndex: number } | { kind: "surcharge" } | { kind: "msl" } | null
	>(null);

	const [isPharmacyModalOpen, setIsPharmacyModalOpen] = useState(false);
	const [expandedSettlementComponents, setExpandedSettlementComponents] = useState<
		Record<string, boolean>
	>({});

	const { data: producers = [] } = useQuery({
		queryKey: ["contracts", "producers-full"],
		queryFn: contractsApi.listProducers,
	});

	const { data: ownerCandidates = [] } = useQuery({
		queryKey: ["contracts", "owner-candidates"],
		queryFn: contractsApi.getOwnerCandidates,
	});

	// Klucz MUSI rozrozniac tryb tworzenia: plaskie ["contracts", undefined]
	// hashowalo sie do ["contracts", null] — tak samo jak klucz listy dla konta
	// bez przepiecia organizacji. Formularz czytal wtedy z cache TABLICE
	// kontraktow i brał ja za wczytywany kontrakt (pusty rok na /kontrakty/nowy
	// po wejsciu z sidebara, 2026-08-13). W trybie edycji numeryczne id — ten
	// sam ksztalt co u pozostalych czytelnikow szczegolow kontraktu, wiec
	// wspoldziela cache i lapie sie na invalidacje po zapisie.
	const { data: contract } = useQuery({
		queryKey: isEdit ? ["contracts", Number(id)] : NEW_CONTRACT_QUERY_KEY,
		queryFn: () => contractsApi.get(Number(id)),
		enabled: isEdit,
	});

	useEffect(() => {
		if (contract) {
			setName(contract.name);
			setProducerId(contract.producerId ?? null);
			setYearInput(String(contract.year));
			setQuarter(contract.quarter);
			setPricingMode(contract.pricingMode || "PURCHASE");
			setOwnerUserId(contract.ownerUserId ?? null);
			setMinTotalSalesValue(
				contract.minTotalSalesValue == null ? "" : String(contract.minTotalSalesValue),
			);
			setMinTotalPurchaseValue(
				contract.minTotalPurchaseValue == null ? "" : String(contract.minTotalPurchaseValue),
			);
			setMinTotalSalesQuantity(
				contract.minTotalSalesQuantity == null ? "" : String(contract.minTotalSalesQuantity),
			);
			setMinTotalPurchaseQuantity(
				contract.minTotalPurchaseQuantity == null ? "" : String(contract.minTotalPurchaseQuantity),
			);
			setMarketingGuaranteed(
				contract.marketingGuaranteed == null ? "" : String(contract.marketingGuaranteed),
			);
			setExposureRealized(
				contract.exposureRealized == null ? "" : String(contract.exposureRealized),
			);
			setExposureDecidedAt(contract.exposureDecidedAt ?? "");
			setExposureNote(contract.exposureNote ?? "");
			setSumSalesPercentsOnPurchase(contract.sumSalesPercentsOnPurchase ?? false);
			setContractTiers(
				(contract.contractTiers ?? []).map((t) => ({
					...t,
					tempId: t.tierId?.toString() || Math.random().toString(36),
				})),
			);
			setPharmacies(
				(contract.pharmacies ?? []).map((p) => ({
					id: 0,
					unitcode: p.unitcode,
					unitname: p.unitname,
					region: p.region || "",
					startDate:
						p.startDate ||
						contract.startDate ||
						getContractDateRange(contract.year, contract.quarter).startDate,
					endDate:
						p.endDate ||
						contract.endDate ||
						getContractDateRange(contract.year, contract.quarter).endDate,
					wlasna: false,
					active: true,
					robotfetchdata: false,
				})),
			);
			setProductSurcharges(
				(contract.productSurcharges ?? []).map((s) => ({
					...s,
					tempId: s.surchargeId?.toString() || Math.random().toString(36),
				})),
			);
			setMslItems(
				(contract.mslItems ?? []).map((m) => ({
					...m,
					tempId: m.itemId?.toString() || Math.random().toString(36),
				})),
			);
			setMslWymagalnoscTryb(
				(contract.mslWymagalnoscTryb === "total" ? "total" : "per_produkt") as
					| "per_produkt"
					| "total",
			);
			setMslProcentWymagalnosciGlobal(
				contract.mslProcentWymagalnosciGlobal == null
					? ""
					: String(contract.mslProcentWymagalnosciGlobal),
			);
			setBrands(
				(contract.brands ?? []).map((brand) => ({
					...brand,
					purchaseValue100Exfactory:
						brand.purchaseValue100Exfactory ?? brand.minPurchaseExfactoryValue ?? 0,
					tempId: brand.brandId?.toString() || Math.random().toString(36),
					settlementTiers: (brand.settlementTiers ?? []).map((tier) => ({
						...tier,
						tempId: tier.tierId?.toString() || Math.random().toString(36),
					})),
				})),
			);
			setBaskets(
				(contract.baskets ?? []).map((b) => ({
					...b,
					tempId: b.basketId?.toString() || Math.random().toString(36),
					products: (b.products ?? []).map((product) => ({
						...product,
						brandClientId: product.brandId == null ? undefined : String(product.brandId),
					})),
					settlements: (b.settlements ?? []).map((s) => ({
						...s,
						tempId: s.settlementId?.toString() || Math.random().toString(36),
						tiers: (s.tiers ?? []).map((t) => ({
							...t,
							tempId: t.tierId?.toString() || Math.random().toString(36),
						})),
					})),
				})),
			);
		} else if (!isEdit) {
			setName("");
			setProducerId(null);
			setYearInput(String(new Date().getFullYear()));
			setQuarter(DEFAULT_CONTRACT_PERIOD);
			setOwnerUserId(null);
			setMinTotalSalesValue("");
			setMinTotalPurchaseValue("");
			setPharmacies([]);
			setBrands([]);
			setProductSurcharges([]);
			setMslItems([]);
			setMslWymagalnoscTryb("per_produkt");
			setMslProcentWymagalnosciGlobal("");
			setBaskets([]);
			setCreateExfactoryIds([]);
		}
	}, [contract, isEdit]);

	// W trybie tworzenia kandydaci zaleza od (producent, year, quarter).
	// Zmiana ktoregokolwiek = wybor staje sie nieaktualny — wymuszamy re-run.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset jest celem efektu
	useEffect(() => {
		if (!isEdit) setCreateExfactoryIds([]);
	}, [isEdit, producerId, year, quarter]);

	const saveMutation = useMutation<{ contractId: number }, Error, CreateContractRequest>({
		mutationFn: (data: CreateContractRequest) =>
			isEdit
				? contractsApi.update(Number(id), data).then(() => ({ contractId: Number(id) }))
				: contractsApi.create(data).then((saved) => ({ contractId: saved.contractId })),
		onSuccess: async (savedContract: { contractId: number }) => {
			const savedContractId = isEdit ? Number(id) : savedContract.contractId;
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["contracts"] }),
				savedContractId
					? queryClient.invalidateQueries({ queryKey: ["contracts", savedContractId] })
					: Promise.resolve(),
			]);
			toast.success("Zmiany zostały zapisane.");
			if (!isEdit && savedContractId) {
				navigate({ to: "/kontrakty/$id", params: { id: String(savedContractId) } });
			}
		},
		onError: (error: Error) => {
			toast.error(`Nie udało się zapisać zmian: ${error.message}`);
		},
	});

	const addBasket = () => {
		setBaskets([
			...baskets,
			{
				name: "",
				products: [],
				settlements: [],
				tempId: Math.random().toString(36),
			},
		]);
	};

	const handleBasketImport = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (ev) => {
			try {
				// Dynamic import xlsx — zmniejsza initial bundle (chunk ~864 KB lazy).
				// loadXlsx centralizuje import-target — chunk-load failure
				// (Sentry 116556881) przepuszczany jest niżej do catch z
				// describeDynamicImportError zamiast cichej propagacji.
				const XLSX = await loadXlsx();
				const data = ev.target?.result;
				const workbook = XLSX.read(data, { type: "binary" });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];
				const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];

				// Mapowanie: nazwa koszyka -> produkty z opcjonalnym brandem
				const basketMap = new Map<string, Array<{ bloz: string; brandName?: string }>>();
				const brandIdByName = new Map(
					brands
						.filter((brand) => brand.name.trim() !== "")
						.map((brand) => [normalizeBrandName(brand.name), brand.tempId]),
				);
				let missingBrandCount = 0;

				// Zaczynamy od i=1, pomijając nagłówek
				for (let i = 1; i < jsonData.length; i++) {
					const row = jsonData[i];
					if (!row || row.length < 1) continue;

					const bloz = String(row[0]).trim();
					// Kolumna koszyka to 4. kolumna (indeks 3)
					const basketName = row[3] ? String(row[3]).trim() : "Koszyk bez nazwy";
					const brandName = String(row[4] ?? "").trim();

					if (/^\d{7}$/.test(bloz)) {
						if (!basketMap.has(basketName)) {
							basketMap.set(basketName, []);
						}
						basketMap.get(basketName)?.push({
							bloz,
							brandName: brandName || undefined,
						});
					}
				}

				if (basketMap.size > 0) {
					const allBloz = Array.from(
						new Set(
							Array.from(basketMap.values()).flatMap((items) => items.map((item) => item.bloz)),
						),
					);

					contractsApi.searchByBlozList(allBloz).then((products) => {
						const productMap = new Map(products.map((p) => [p.bloz07, p]));

						const newBaskets: BasketWithTempId[] = Array.from(basketMap.entries()).map(
							([name, importedItems]) => ({
								name,
								tempId: Math.random().toString(36),
								products: importedItems
									.map(({ bloz, brandName }) => {
										const p = productMap.get(bloz);
										if (!p) return null;
										const brandClientId = brandName
											? brandIdByName.get(normalizeBrandName(brandName))
											: undefined;
										if (brandName && !brandClientId) {
											missingBrandCount++;
										}
										return {
											bloz07: p.bloz07,
											name: p.nazwa, // Mapujemy nazwa -> name dla spójności z UI
											brandClientId,
										};
									})
									.filter((product): product is NonNullable<typeof product> => product !== null)
									.filter(
										(product, index, array) =>
											array.findIndex((candidate) => candidate.bloz07 === product.bloz07) === index,
									),
								settlements: [],
							}),
						);

						setBaskets((prev) => [...prev, ...newBaskets]);
						if (missingBrandCount > 0) {
							toast.warning(
								`Zaimportowano ${newBaskets.length} koszyków. ${missingBrandCount} pozycji nie otrzymało brandu, bo nazwa z pliku nie pasowała do zdefiniowanych brandów.`,
							);
							return;
						}
						toast.success(`Pomyślnie zaimportowano ${newBaskets.length} koszyków.`);
					});
				} else {
					toast.error("Nie znaleziono poprawnych kodów BLOZ w pliku.");
				}
			} catch (err) {
				if (attemptDynamicImportRecovery(err)) return;
				console.error("Błąd importu koszyków:", err);
				toast.error(describeDynamicImportError(err, "Błąd podczas odczytu pliku szablonu"));
			}
		};
		reader.readAsBinaryString(file);
	};

	const exportBasketProducts = async () => {
		// Kolejnosc kolumn zgodna z szablonem importu koszykow (handleBasketImport),
		// zeby wyeksportowany plik dalo sie wczytac przez "Importuj koszyki z szablonu":
		// BLOZ | Nazwa produktu | Cena ex-factory | Nazwa koszyka | Brand.
		const brandNameByTempId = new Map(brands.map((brand) => [brand.tempId, brand.name]));
		const rows = baskets.flatMap((basket) =>
			basket.products.map((product) => [
				product.bloz07,
				product.name,
				"", // Cena ex-factory — nie dotyczy produktu w koszyku
				basket.name || "Koszyk bez nazwy",
				product.brandClientId ? (brandNameByTempId.get(product.brandClientId) ?? "") : "",
			]),
		);

		if (rows.length === 0) {
			toast.error("Brak produktów w koszykach do eksportu.");
			return;
		}

		try {
			const XLSX = await loadXlsx();
			const worksheet = XLSX.utils.aoa_to_sheet([
				["BLOZ", "Nazwa produktu", "Cena ex-factory", "Nazwa koszyka", "Brand"],
				...rows,
			]);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Produkty koszykow");
			XLSX.writeFile(
				workbook,
				`kontrakt-koszyki-produkty-${isEdit ? id : "nowy"}-${year}-${quarter}.xlsx`,
			);
		} catch (err) {
			if (attemptDynamicImportRecovery(err)) return;
			console.error("Błąd eksportu produktów koszyków:", err);
			toast.error(
				describeDynamicImportError(err, "Nie udało się wyeksportować produktów z koszyków"),
			);
		}
	};

	// Eksport dopłat — kolejnosc kolumn zgodna z importem (handleSurchargeImport):
	// BLOZ | sell-in % | sell-in zł | sell-out % | sell-out zł. Nazwa na koncu (import ja pomija).
	const exportSurcharges = async () => {
		if (productSurcharges.length === 0) {
			toast.error("Brak dopłat do eksportu.");
			return;
		}
		try {
			const XLSX = await loadXlsx();
			const rows = productSurcharges.map((s) => [
				s.bloz07,
				s.doplataSellInProcent ?? "",
				s.doplataSellInKwota ?? "",
				s.doplataSellOutProcent ?? "",
				s.doplataSellOutKwota ?? "",
				s.name,
			]);
			const worksheet = XLSX.utils.aoa_to_sheet([
				[
					"BLOZ",
					"Dopłata sell-in %",
					"Dopłata sell-in zł",
					"Dopłata sell-out %",
					"Dopłata sell-out zł",
					"Nazwa produktu",
				],
				...rows,
			]);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Doplaty");
			XLSX.writeFile(workbook, `kontrakt-doplaty-${isEdit ? id : "nowy"}-${year}-${quarter}.xlsx`);
		} catch (err) {
			if (attemptDynamicImportRecovery(err)) return;
			console.error("Błąd eksportu dopłat:", err);
			toast.error(describeDynamicImportError(err, "Nie udało się wyeksportować dopłat"));
		}
	};

	// Eksport MSL — kolejnosc kolumn zgodna z importem (handleMslImport):
	// Nazwa | BLOZ | MSL dostepnosc | % wymagalnosci.
	const exportMslItems = async () => {
		if (mslItems.length === 0) {
			toast.error("Brak pozycji MSL do eksportu.");
			return;
		}
		try {
			const XLSX = await loadXlsx();
			const rows = mslItems.map((m) => [
				m.name,
				m.bloz07,
				m.mslDostepnosc,
				m.procentWymagalnosci ?? "",
			]);
			const worksheet = XLSX.utils.aoa_to_sheet([
				["Nazwa produktu", "BLOZ", "MSL dostępność (szt.)", "% wymagalności"],
				...rows,
			]);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "MSL");
			XLSX.writeFile(workbook, `kontrakt-msl-${isEdit ? id : "nowy"}-${year}-${quarter}.xlsx`);
		} catch (err) {
			if (attemptDynamicImportRecovery(err)) return;
			console.error("Błąd eksportu MSL:", err);
			toast.error(describeDynamicImportError(err, "Nie udało się wyeksportować listy MSL"));
		}
	};

	// Eksport brandów — format zgodny z importem (handleBrandImport):
	// Nazwa | Minimum | Wartość 100% | <kolumny progów: "100%", "110%", ...>.
	// Komórka progu: procent -> "X%", kwota -> "X zł" (parser: parseBrandTierImportCell).
	const exportBrands = async () => {
		if (brands.length === 0) {
			toast.error("Brak brandów do eksportu.");
			return;
		}
		try {
			const XLSX = await loadXlsx();
			const thresholds = Array.from(
				new Set(brands.flatMap((b) => (b.settlementTiers ?? []).map((t) => t.thresholdPercent))),
			).sort((a, b) => a - b);
			const header = ["Nazwa", "Minimum", "Wartość 100%", ...thresholds.map((t) => `${t}%`)];
			const rows = brands.map((brand) => {
				const tierByThreshold = new Map(
					(brand.settlementTiers ?? []).map((t) => [t.thresholdPercent, t]),
				);
				const tierCells = thresholds.map((t) => {
					const tier = tierByThreshold.get(t);
					if (!tier) return "";
					if (tier.settlementPercent != null) return `${tier.settlementPercent}%`;
					if (tier.settlementAmount != null) return `${tier.settlementAmount} zł`;
					return "";
				});
				return [
					brand.name,
					brand.minPurchaseExfactoryValue ?? "",
					brand.purchaseValue100Exfactory ?? "",
					...tierCells,
				];
			});
			const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Brandy");
			XLSX.writeFile(workbook, `kontrakt-brandy-${isEdit ? id : "nowy"}-${year}-${quarter}.xlsx`);
		} catch (err) {
			if (attemptDynamicImportRecovery(err)) return;
			console.error("Błąd eksportu brandów:", err);
			toast.error(describeDynamicImportError(err, "Nie udało się wyeksportować brandów"));
		}
	};

	const addBrand = () => {
		setBrands((prev) => [
			...prev,
			{
				tempId: Math.random().toString(36),
				name: "",
				minPurchaseExfactoryValue: 0,
				purchaseValue100Exfactory: 0,
				settlementTiers: [],
			},
		]);
	};

	const updateBrand = (tempId: string, data: Partial<ContractBrandWithTempId>) => {
		setBrands((prev) =>
			prev.map((brand) => (brand.tempId === tempId ? { ...brand, ...data } : brand)),
		);
	};

	const removeBrand = (tempId: string) => {
		setBrands((prev) => prev.filter((brand) => brand.tempId !== tempId));
		setBaskets((prev) =>
			prev.map((basket) => ({
				...basket,
				products: basket.products.map((product) =>
					product.brandClientId === tempId ? { ...product, brandClientId: undefined } : product,
				),
			})),
		);
	};

	const addBrandSettlementTier = (brandTempId: string) => {
		setBrands((prev) =>
			prev.map((brand) =>
				brand.tempId === brandTempId
					? {
							...brand,
							settlementTiers: [
								...(brand.settlementTiers ?? []),
								{
									tempId: Math.random().toString(36),
									thresholdPercent: 100,
									settlementPercent: undefined,
									settlementAmount: 0,
								},
							],
						}
					: brand,
			),
		);
	};

	const updateBrandSettlementTier = (
		brandTempId: string,
		tierTempId: string,
		data: Partial<ContractBrandSettlementTierWithTempId>,
	) => {
		setBrands((prev) =>
			prev.map((brand) =>
				brand.tempId === brandTempId
					? {
							...brand,
							settlementTiers: (brand.settlementTiers ?? []).map((tier) =>
								tier.tempId === tierTempId ? { ...tier, ...data } : tier,
							),
						}
					: brand,
			),
		);
	};

	const removeBrandSettlementTier = (brandTempId: string, tierTempId: string) => {
		setBrands((prev) =>
			prev.map((brand) =>
				brand.tempId === brandTempId
					? {
							...brand,
							settlementTiers: (brand.settlementTiers ?? []).filter(
								(tier) => tier.tempId !== tierTempId,
							),
						}
					: brand,
			),
		);
	};

	const downloadBrandImportTemplate = async () => {
		try {
			const XLSX = await loadXlsx();
			const worksheet = XLSX.utils.aoa_to_sheet([
				["Nazwa", "Minimum", "Wartość 100%", "100%", "110%"],
				["Betaloc", 9500, 12000, "2,5%", "3000 zł"],
				["Lercan", 12000, 15000, "1500 zł", "4,25%"],
			]);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Brandy");
			XLSX.writeFile(workbook, "import-brandy-kontrakt.xlsx");
		} catch (err) {
			if (attemptDynamicImportRecovery(err)) return;
			console.error("Błąd generowania przykładu importu brandów:", err);
			toast.error(
				describeDynamicImportError(err, "Nie udało się przygotować przykładowego pliku brandów"),
			);
		}
	};

	const handleBrandImport = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (ev) => {
			try {
				const XLSX = await loadXlsx();
				const data = ev.target?.result;
				const workbook = XLSX.read(data, { type: "binary" });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];
				// raw: false -> bierzemy sformatowany tekst komorki (".w"). Komorki progow
				// z formatem procentowym Excela trzymaja ulamek (0,77% = 0.0077); bez tego
				// parser dostawal liczbe bez jednostki i odrzucal ("musi miec jednostke %").
				const rows = XLSX.utils.sheet_to_json(worksheet, {
					header: 1,
					raw: false,
				}) as unknown[][];
				const [headerRow, ...dataRows] = rows;
				if (!headerRow || headerRow.length === 0) {
					toast.error("Plik importu brandów nie zawiera nagłówków.");
					return;
				}

				const normalizedHeaders = headerRow.map((cell) => normalizeImportHeader(cell));
				const nameIndex = normalizedHeaders.indexOf("nazwa");
				const minimumIndex = normalizedHeaders.indexOf("minimum");
				const value100Index = normalizedHeaders.findIndex(
					(header) =>
						header === "wartość 100%" ||
						header === "wartosc 100%" ||
						header === "wartość 100% zakupu ex-factory" ||
						header === "wartosc 100% zakupu ex-factory",
				);
				const thresholdColumns = headerRow
					.map((cell, index) => ({
						index,
						thresholdPercent: parseThresholdHeader(cell),
					}))
					.filter(
						(column): column is { index: number; thresholdPercent: number } =>
							column.thresholdPercent != null,
					);

				if (nameIndex === -1 || minimumIndex === -1 || value100Index === -1) {
					toast.error("Plik brandów musi zawierać kolumny 'Nazwa', 'Minimum' oraz 'Wartość 100%'.");
					return;
				}

				if (thresholdColumns.length === 0) {
					toast.error(
						"Plik brandów musi zawierać co najmniej jedną kolumnę progu, np. 100% lub 110%.",
					);
					return;
				}

				const importedBrands = dataRows
					.map((row, rowIndex) => {
						const rowNumber = rowIndex + 2;
						const name = String(row?.[nameIndex] ?? "").trim();
						if (!name) return null;

						const minPurchaseExfactoryValue =
							parseOptionalNumber(String(row?.[minimumIndex] ?? "")) ?? 0;
						const purchaseValue100Exfactory =
							parseOptionalNumber(String(row?.[value100Index] ?? "")) ?? 0;

						const settlementTiers = thresholdColumns
							.map(({ index, thresholdPercent }) =>
								parseBrandTierImportCell(row?.[index], thresholdPercent, rowNumber),
							)
							.filter(Boolean) as ParsedBrandSettlementTierImport[];

						return {
							tempId: Math.random().toString(36),
							name,
							purchaseValue100Exfactory,
							minPurchaseExfactoryValue,
							settlementTiers: settlementTiers.map((tier) => ({
								...tier,
								tempId: Math.random().toString(36),
							})),
						};
					})
					.filter(Boolean) as ContractBrandWithTempId[];

				if (importedBrands.length === 0) {
					toast.error("Nie znaleziono brandów w pliku.");
					return;
				}

				const existingNames = new Set(brands.map((brand) => normalizeBrandName(brand.name)));
				const uniqueImportedBrands = importedBrands.filter(
					(brand) => !existingNames.has(normalizeBrandName(brand.name)),
				);
				setBrands((prev) => [...prev, ...uniqueImportedBrands]);
				toast.success(`Zaimportowano ${uniqueImportedBrands.length} brandów.`);
			} catch (err) {
				if (attemptDynamicImportRecovery(err)) return;
				console.error("Błąd importu brandów:", err);
				toast.error(
					err instanceof Error
						? err.message
						: describeDynamicImportError(err, "Błąd podczas odczytu pliku brandów"),
				);
			} finally {
				e.target.value = "";
			}
		};
		reader.readAsBinaryString(file);
	};

	// Import dopłat do produktów. 5 kolumn w stalej kolejnosci zgodnej z sekcja UI:
	// BLOZ, sell-in %, sell-in zl, sell-out %, sell-out zl. Pierwszy wiersz = naglowek
	// (pomijany). Nazwy produktow rozwiazujemy z bazy po kodzie BLOZ.
	const handleSurchargeImport = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (ev) => {
			try {
				const XLSX = await loadXlsx();
				const data = ev.target?.result;
				const workbook = XLSX.read(data, { type: "binary" });
				const worksheet = workbook.Sheets[workbook.SheetNames[0]];
				const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
				if (rows.length < 2) {
					toast.error("Plik dopłat nie zawiera danych (oczekiwany nagłówek + min. 1 wiersz).");
					return;
				}

				const parsedByBloz = new Map<
					string,
					{
						doplataSellInProcent?: number;
						doplataSellInKwota?: number;
						doplataSellOutProcent?: number;
						doplataSellOutKwota?: number;
					}
				>();

				for (let i = 1; i < rows.length; i++) {
					const row = rows[i];
					if (!row || row.length === 0) continue;
					const bloz = String(row[0] ?? "").trim();
					if (!/^\d{7}$/.test(bloz)) continue;
					parsedByBloz.set(bloz, {
						doplataSellInProcent: parseOptionalNumber(String(row[1] ?? "")),
						doplataSellInKwota: parseOptionalNumber(String(row[2] ?? "")),
						doplataSellOutProcent: parseOptionalNumber(String(row[3] ?? "")),
						doplataSellOutKwota: parseOptionalNumber(String(row[4] ?? "")),
					});
				}

				if (parsedByBloz.size === 0) {
					toast.error("Nie znaleziono poprawnych kodów BLOZ (7 cyfr) w pierwszej kolumnie.");
					return;
				}

				const products = await contractsApi.searchByBlozList(Array.from(parsedByBloz.keys()));
				const productMap = new Map(products.map((p) => [p.bloz07, p]));
				const existingBloz = new Set(productSurcharges.map((item) => item.bloz07));

				let missingProductCount = 0;
				const newSurcharges: ProductSurchargeWithTempId[] = [];
				for (const [bloz, values] of parsedByBloz.entries()) {
					if (existingBloz.has(bloz)) continue;
					const product = productMap.get(bloz);
					if (!product) {
						missingProductCount++;
						continue;
					}
					newSurcharges.push({
						tempId: Math.random().toString(36),
						bloz07: bloz,
						name: product.nazwa,
						...values,
					});
				}

				if (newSurcharges.length === 0) {
					toast.error(
						"Brak nowych produktów do dodania (wszystkie już na liście lub nie znaleziono w bazie).",
					);
					return;
				}

				setProductSurcharges((prev) => [...prev, ...newSurcharges]);
				if (missingProductCount > 0) {
					toast.warning(
						`Zaimportowano ${newSurcharges.length} dopłat. ${missingProductCount} kodów BLOZ nie znaleziono w bazie.`,
					);
				} else {
					toast.success(`Zaimportowano ${newSurcharges.length} dopłat do produktów.`);
				}
			} catch (err) {
				if (attemptDynamicImportRecovery(err)) return;
				console.error("Błąd importu dopłat:", err);
				toast.error(describeDynamicImportError(err, "Błąd podczas odczytu pliku dopłat"));
			} finally {
				e.target.value = "";
			}
		};
		reader.readAsBinaryString(file);
	};

	// Import MSL. 4 kolumny: Nazwa (ignorowana), BLOZ, MSL dostepnosc (szt.), % wymagalnosci.
	// Kolumny 2, 3, 4 sa czytane; kolumna 1 (nazwa) pomijana. Pierwszy wiersz = naglowek.
	const handleMslImport = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (ev) => {
			try {
				const XLSX = await loadXlsx();
				const data = ev.target?.result;
				const workbook = XLSX.read(data, { type: "binary" });
				const worksheet = workbook.Sheets[workbook.SheetNames[0]];
				const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
				if (rows.length < 2) {
					toast.error("Plik MSL nie zawiera danych (oczekiwany nagłówek + min. 1 wiersz).");
					return;
				}

				const parsedByBloz = new Map<
					string,
					{ mslDostepnosc: number; procentWymagalnosci?: number }
				>();

				for (let i = 1; i < rows.length; i++) {
					const row = rows[i];
					if (!row || row.length < 2) continue;
					// col[0] = nazwa (ignorowana), col[1] = bloz, col[2] = msl_dostepnosc, col[3] = procent
					const bloz = String(row[1] ?? "").trim();
					if (!/^\d{7}$/.test(bloz)) continue;
					parsedByBloz.set(bloz, {
						mslDostepnosc: Number.parseInt(String(row[2] ?? "0"), 10) || 0,
						procentWymagalnosci: parseOptionalNumber(String(row[3] ?? "")),
					});
				}

				if (parsedByBloz.size === 0) {
					toast.error("Nie znaleziono poprawnych kodów BLOZ (7 cyfr) w drugiej kolumnie.");
					return;
				}

				const products = await contractsApi.searchByBlozList(Array.from(parsedByBloz.keys()));
				const productMap = new Map(products.map((p) => [p.bloz07, p]));
				const existingBloz = new Set(mslItems.map((item) => item.bloz07));

				let missingProductCount = 0;
				const newItems: MslItemWithTempId[] = [];
				for (const [bloz, values] of parsedByBloz.entries()) {
					if (existingBloz.has(bloz)) continue;
					const product = productMap.get(bloz);
					if (!product) {
						missingProductCount++;
						continue;
					}
					newItems.push({
						tempId: Math.random().toString(36),
						bloz07: bloz,
						name: product.nazwa,
						...values,
					});
				}

				if (newItems.length === 0) {
					toast.error(
						"Brak nowych produktów do dodania (wszystkie już na liście lub nie znaleziono w bazie).",
					);
					return;
				}

				setMslItems((prev) => [...prev, ...newItems]);
				if (missingProductCount > 0) {
					toast.warning(
						`Zaimportowano ${newItems.length} pozycji MSL. ${missingProductCount} kodów BLOZ nie znaleziono w bazie.`,
					);
				} else {
					toast.success(`Zaimportowano ${newItems.length} pozycji MSL.`);
				}
			} catch (err) {
				if (attemptDynamicImportRecovery(err)) return;
				console.error("Błąd importu MSL:", err);
				toast.error(describeDynamicImportError(err, "Błąd podczas odczytu pliku MSL"));
			} finally {
				e.target.value = "";
			}
		};
		reader.readAsBinaryString(file);
	};

	const removeBasket = (index: number) => {
		setBaskets(baskets.filter((_, i) => i !== index));
	};

	const updateBasket = (index: number, data: Partial<BasketWithTempId>) => {
		const newBaskets = [...baskets];
		newBaskets[index] = { ...newBaskets[index], ...data };
		setBaskets(newBaskets);
	};

	const addSettlement = (basketIndex: number) => {
		const basket = baskets[basketIndex];
		if (basket.settlements.length >= 4) return;

		const newSettlements = [
			...basket.settlements,
			{
				tempId: Math.random().toString(36),
				startDate: `${year}-01-01`,
				endDate: `${year}-12-31`,
				tiers: [],
			},
		];
		updateBasket(basketIndex, { settlements: newSettlements });
	};

	const removeSettlement = (basketIndex: number, settlementIndex: number) => {
		const basket = baskets[basketIndex];
		const newSettlements = basket.settlements.filter((_, i) => i !== settlementIndex);
		updateBasket(basketIndex, { settlements: newSettlements });
	};

	const updateSettlement = (
		basketIndex: number,
		settlementIndex: number,
		data: Partial<SettlementWithTempId>,
	) => {
		const basket = baskets[basketIndex];
		const newSettlements = [...basket.settlements];
		newSettlements[settlementIndex] = { ...newSettlements[settlementIndex], ...data };
		updateBasket(basketIndex, { settlements: newSettlements });
	};

	const addTier = (basketIndex: number, settlementIndex: number) => {
		const basket = baskets[basketIndex];
		const settlement = basket.settlements[settlementIndex];
		const newTiers = [
			...settlement.tiers,
			{
				tempId: Math.random().toString(36),
				salesFrom: 0,
				purchaseFrom: 0,
				salesPercent: 0,
				purchasePercent: 0,
			},
		];
		updateSettlement(basketIndex, settlementIndex, { tiers: newTiers });
	};

	const removeTier = (basketIndex: number, settlementIndex: number, tierIndex: number) => {
		const basket = baskets[basketIndex];
		const settlement = basket.settlements[settlementIndex];
		const newTiers = settlement.tiers.filter((_, i) => i !== tierIndex);
		updateSettlement(basketIndex, settlementIndex, { tiers: newTiers });
	};

	const updateTier = (
		basketIndex: number,
		settlementIndex: number,
		tierIndex: number,
		data: Partial<SettlementTierWithTempId>,
	) => {
		const basket = baskets[basketIndex];
		const settlement = basket.settlements[settlementIndex];
		const newTiers = [...settlement.tiers];
		newTiers[tierIndex] = { ...newTiers[tierIndex], ...data };
		updateSettlement(basketIndex, settlementIndex, { tiers: newTiers });
	};

	const addContractTier = () => {
		setContractTiers((prev) => [
			...prev,
			{
				tempId: Math.random().toString(36),
				salesFrom: 0,
				purchaseFrom: 0,
				salesPercent: 0,
				purchasePercent: 0,
			},
		]);
	};

	const updateContractTier = (tempId: string, data: Partial<SettlementTierWithTempId>) => {
		setContractTiers((prev) => prev.map((t) => (t.tempId === tempId ? { ...t, ...data } : t)));
	};

	const removeContractTier = (tempId: string) => {
		setContractTiers((prev) => prev.filter((t) => t.tempId !== tempId));
	};

	const toggleSettlementComponents = (settlementTempId: string) => {
		setExpandedSettlementComponents((prev) => ({
			...prev,
			[settlementTempId]: !prev[settlementTempId],
		}));
	};

	const handleProductsSelect = (selectedProducts: ProductItem[]) => {
		if (!productSelectionTarget) return;

		if (productSelectionTarget.kind === "basket") {
			const basket = baskets[productSelectionTarget.basketIndex];
			if (!basket) {
				setProductSelectionTarget(null);
				return;
			}
			const newProducts = selectNewProductsForBasket(basket, selectedProducts);

			if (newProducts.length > 0) {
				updateBasket(productSelectionTarget.basketIndex, {
					products: [...basket.products, ...newProducts],
				});
			}
		} else if (productSelectionTarget.kind === "surcharge") {
			const existingBloz = new Set(productSurcharges.map((item) => item.bloz07));
			const newSurcharges = selectedProducts
				.filter((product) => !existingBloz.has(product.bloz07))
				.map((product) => ({
					tempId: Math.random().toString(36),
					bloz07: product.bloz07,
					name: product.nazwa,
					doplataSellInProcent: undefined,
					doplataSellInKwota: undefined,
					doplataSellOutProcent: undefined,
					doplataSellOutKwota: undefined,
				}));
			if (newSurcharges.length > 0) {
				setProductSurcharges((prev) => [...prev, ...newSurcharges]);
			}
		} else if (productSelectionTarget.kind === "msl") {
			const existingBloz = new Set(mslItems.map((item) => item.bloz07));
			const newItems = selectedProducts
				.filter((product) => !existingBloz.has(product.bloz07))
				.map((product) => ({
					tempId: Math.random().toString(36),
					bloz07: product.bloz07,
					name: product.nazwa,
					mslDostepnosc: 0,
					procentWymagalnosci: undefined,
				}));
			if (newItems.length > 0) {
				setMslItems((prev) => [...prev, ...newItems]);
			}
		}

		setProductSelectionTarget(null);
	};

	const updateProductSurcharge = (tempId: string, data: Partial<ProductSurchargeWithTempId>) => {
		setProductSurcharges((prev) =>
			prev.map((item) => (item.tempId === tempId ? { ...item, ...data } : item)),
		);
	};

	const removeProductSurcharge = (tempId: string) => {
		setProductSurcharges((prev) => prev.filter((item) => item.tempId !== tempId));
	};

	const updateMslItem = (tempId: string, data: Partial<MslItemWithTempId>) => {
		setMslItems((prev) =>
			prev.map((item) => (item.tempId === tempId ? { ...item, ...data } : item)),
		);
	};

	const removeMslItem = (tempId: string) => {
		setMslItems((prev) => prev.filter((item) => item.tempId !== tempId));
	};

	// Domyslny zakres dat apteki = okres kontraktu. Ta sama wartosc trafia do
	// aptek dodanych z selektora, do wierszy importu bez kolumn "Od"/"Do" oraz do
	// dymka z instrukcja importu — zeby instrukcja nie obiecywala czegos innego,
	// niz robi kod.
	const pharmacyDefaultRange = useMemo(() => getContractDateRange(year, quarter), [year, quarter]);

	// Zakres z paska "Daty dla wszystkich". Startuje z okresu kontraktu i podaza
	// za zmiana Rok/Okres — inaczej tuz po przelaczeniu kwartalu pasek
	// podpowiadalby zakres poprzedniego.
	const [bulkStartDate, setBulkStartDate] = useState(pharmacyDefaultRange.startDate);
	const [bulkEndDate, setBulkEndDate] = useState(pharmacyDefaultRange.endDate);
	useEffect(() => {
		setBulkStartDate(pharmacyDefaultRange.startDate);
		setBulkEndDate(pharmacyDefaultRange.endDate);
	}, [pharmacyDefaultRange]);

	const handlePharmaciesSelect = (selected: PharmacyItem[]) => {
		const existingCodes = new Set(pharmacies.map((p) => p.unitcode));
		const contractDateRange = pharmacyDefaultRange;
		const newPharmacies = selected.filter((p) => !existingCodes.has(p.unitcode));
		if (newPharmacies.length > 0) {
			setPharmacies([
				...pharmacies,
				...newPharmacies.map((pharmacy) => ({
					...pharmacy,
					startDate: contractDateRange.startDate,
					endDate: contractDateRange.endDate,
				})),
			]);
		}
		setIsPharmacyModalOpen(false);
	};

	const exportPharmacies = async () => {
		if (pharmacies.length === 0) {
			toast.error("Brak aptek do eksportu.");
			return;
		}

		try {
			const XLSX = await loadXlsx();
			const worksheet = XLSX.utils.aoa_to_sheet([
				["Nazwa apteki", "KSID", "Region", "Od", "Do"],
				...pharmacies.map((pharmacy) => [
					pharmacy.unitname,
					pharmacy.unitcode,
					pharmacy.region ?? "",
					pharmacy.startDate,
					pharmacy.endDate,
				]),
			]);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Apteki");
			XLSX.writeFile(workbook, `kontrakt-apteki-${isEdit ? id : "nowy"}-${year}-${quarter}.xlsx`);
		} catch (err) {
			if (attemptDynamicImportRecovery(err)) return;
			console.error("Błąd eksportu aptek:", err);
			toast.error(describeDynamicImportError(err, "Nie udało się wyeksportować listy aptek"));
		}
	};

	const handlePharmacyImport = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (ev) => {
			try {
				const XLSX = await loadXlsx();
				const data = ev.target?.result;
				const workbook = XLSX.read(data, { type: "binary" });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];
				const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
				const [headerRow, ...dataRows] = rows;

				if (!headerRow || headerRow.length === 0) {
					toast.error("Plik importu aptek nie zawiera nagłówków.");
					return;
				}

				const normalizedHeaders = headerRow.map((cell) => normalizeImportHeader(cell));
				const unitcodeIndex = normalizedHeaders.findIndex((header) =>
					["ksid", "unitcode", "kod apteki", "apteka id"].includes(header),
				);
				const startDateIndex = normalizedHeaders.findIndex((header) =>
					["od", "data od", "start", "start date"].includes(header),
				);
				const endDateIndex = normalizedHeaders.findIndex((header) =>
					["do", "data do", "koniec", "end date"].includes(header),
				);

				if (unitcodeIndex === -1) {
					toast.error("Plik aptek musi zawierać kolumnę 'KSID'.");
					return;
				}

				const contractDateRange = pharmacyDefaultRange;
				// Kody o złym formacie (np. Excel uciął wiodące zero: 070151 → 70151)
				// odsiewamy przed wysyłką — inaczej wracają jako "brak apteki w FM2"
				// i użytkownik szuka problemu po niewłaściwej stronie.
				const invalidCodes = dataRows
					.map((row) => String(row?.[unitcodeIndex] ?? "").trim())
					.filter((unitcode) => unitcode !== "" && !KSID_FORMAT.isValid(unitcode));
				const importedRows = dataRows
					.map((row) => {
						const unitcode = String(row?.[unitcodeIndex] ?? "").trim();
						if (!unitcode || !KSID_FORMAT.isValid(unitcode)) return null;
						return {
							unitcode,
							startDate: parseImportDate(row?.[startDateIndex]) ?? contractDateRange.startDate,
							endDate: parseImportDate(row?.[endDateIndex]) ?? contractDateRange.endDate,
						};
					})
					.filter((row): row is NonNullable<typeof row> => row !== null);

				if (importedRows.length === 0) {
					// Plik z samymi uciętymi zerami wygląda jak plik bez aptek —
					// panel musi powiedzieć, które kody odrzuciliśmy i dlaczego.
					if (invalidCodes.length > 0) {
						setPharmacyImportReport(reconcileCodeImport([], [], invalidCodes));
						toast.error(
							`Żaden kod w pliku nie jest poprawnym KSID (5 lub 6 cyfr). Odrzucono ${invalidCodes.length}.`,
						);
						return;
					}
					toast.error("Nie znaleziono aptek w pliku.");
					return;
				}

				const byUnitcode = new Map(importedRows.map((row) => [row.unitcode, row]));
				const importedPharmacies = await contractsApi.searchByUnitCodes(
					Array.from(byUnitcode.keys()),
				);
				const importedMap = new Map(
					importedPharmacies.map((pharmacy) => [pharmacy.unitcode, pharmacy]),
				);

				// Liczymy na surowej liście wierszy (przed Map), żeby rozdzielić
				// powtórzenia w pliku od KSID nieznanych w FM2.
				const report = reconcileCodeImport(
					importedRows.map((row) => row.unitcode),
					importedPharmacies.map((pharmacy) => pharmacy.unitcode),
					invalidCodes,
				);
				setPharmacyImportReport(report);
				const missingCodes = report.unmatchedCodes;
				const mergedByUnitcode = new Map(
					pharmacies.map((pharmacy) => [pharmacy.unitcode, pharmacy]),
				);

				for (const [unitcode, importRow] of byUnitcode.entries()) {
					const pharmacy = importedMap.get(unitcode);
					if (!pharmacy) continue;
					mergedByUnitcode.set(unitcode, {
						...pharmacy,
						startDate: importRow.startDate,
						endDate: importRow.endDate,
					});
				}

				setPharmacies(Array.from(mergedByUnitcode.values()));

				if (missingCodes.length > 0 || invalidCodes.length > 0) {
					const problems = [
						missingCodes.length > 0 ? `${missingCodes.length} KSID nieznanych w FM2` : null,
						invalidCodes.length > 0
							? `${invalidCodes.length} o niepoprawnym formacie (oczekiwane 5 lub 6 cyfr)`
							: null,
					].filter(Boolean);
					toast.warning(
						`Zaimportowano ${importedMap.size} aptek. Pominięto: ${problems.join(", ")}.`,
						{ description: "Pełna lista pominiętych kodów jest nad tabelą aptek." },
					);
					return;
				}

				toast.success(`Zaimportowano ${importedMap.size} aptek.`);
			} catch (err) {
				if (attemptDynamicImportRecovery(err)) return;
				console.error("Błąd importu aptek:", err);
				toast.error(
					err instanceof Error
						? err.message
						: describeDynamicImportError(err, "Błąd podczas odczytu pliku aptek"),
				);
			} finally {
				e.target.value = "";
			}
		};
		reader.readAsBinaryString(file);
	};

	const removePharmacy = (unitcode: string) => {
		setPharmacies(pharmacies.filter((p) => p.unitcode !== unitcode));
	};

	const updatePharmacyDate = (unitcode: string, field: "startDate" | "endDate", value: string) => {
		setPharmacies((prev) =>
			prev.map((pharmacy) =>
				pharmacy.unitcode === unitcode ? { ...pharmacy, [field]: value } : pharmacy,
			),
		);
	};

	const filteredSelectedPharmacies = useMemo(() => {
		if (!pharmacySearchQuery) return pharmacies;
		const q = pharmacySearchQuery.toLowerCase();
		return pharmacies.filter(
			(p) =>
				p.unitname.toLowerCase().includes(q) ||
				p.unitcode.toLowerCase().includes(q) ||
				p.region?.toLowerCase().includes(q),
		);
	}, [pharmacies, pharmacySearchQuery]);

	// Hurtowa zmiana dat dziala na tym, co uzytkownik widzi: przy aktywnej
	// wyszukiwarce tylko na odfiltrowanych wierszach (przycisk mowi to wprost),
	// bez filtra na calej liscie.
	const isPharmacyRangeCustom =
		bulkStartDate !== pharmacyDefaultRange.startDate ||
		bulkEndDate !== pharmacyDefaultRange.endDate;

	const applyBulkPharmacyDates = () => {
		const error = validatePharmacyDateRange(bulkStartDate, bulkEndDate);
		if (error) {
			toast.error(error);
			return;
		}
		const targets = new Set(filteredSelectedPharmacies.map((p) => p.unitcode));
		if (targets.size === 0) {
			toast.error("Brak aptek do ustawienia dat.");
			return;
		}
		setPharmacies((prev) =>
			setPharmacyDatesFor(prev, targets, { startDate: bulkStartDate, endDate: bulkEndDate }),
		);
		toast.success(
			`Ustawiono daty ${bulkStartDate} … ${bulkEndDate} dla ${targets.size} ${
				targets.size === 1 ? "apteki" : "aptek"
			}.`,
		);
	};

	// Czyszczenie calego koszyka: kopiowanie kontraktu czesto konczy sie
	// wymiana listy produktow w jednym koszyku (usun -> zaczytaj ponownie),
	// a klikanie 68 krzyzykow po kolei to nie jest droga.
	const clearBasketProducts = (basketIndex: number) => {
		updateBasket(basketIndex, { products: [] });
		setClearProductsTarget(null);
	};

	const removeProductFromBasket = (basketIndex: number, bloz07: string) => {
		const basket = baskets[basketIndex];
		updateBasket(basketIndex, {
			products: basket.products.filter((p) => p.bloz07 !== bloz07),
		});
	};

	const updateBasketProductBrand = (
		basketIndex: number,
		bloz07: string,
		brandClientId?: string,
	) => {
		const basket = baskets[basketIndex];
		updateBasket(basketIndex, {
			products: basket.products.map((product) =>
				product.bloz07 === bloz07 ? { ...product, brandClientId } : product,
			),
		});
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		// Jeśli cenniki są dirty, zapisz je razem z kontraktem (fire-and-forget — błąd
		// wyświetli toast z ExFactoryPriceListsSection, główny zapis kontraktu idzie dalej).
		pendingExfactorySaveRef.current?.().catch(() => undefined);
		if (producerId == null) {
			toast.error("Wybierz producenta.");
			return;
		}
		const brandNames = new Set<string>();
		for (const brand of brands) {
			const normalizedName = brand.name.trim().toLowerCase();
			if (!normalizedName) {
				toast.error("Nazwa brandu nie może być pusta.");
				return;
			}
			if (brandNames.has(normalizedName)) {
				toast.error(`Brand "${brand.name}" jest zdefiniowany więcej niż raz.`);
				return;
			}
			brandNames.add(normalizedName);
			if ((brand.minPurchaseExfactoryValue ?? 0) < 0) {
				toast.error(`Minimalna wartość planu dla brandu "${brand.name}" nie może być ujemna.`);
				return;
			}
			if ((brand.purchaseValue100Exfactory ?? 0) < 0) {
				toast.error(
					`Wartość 100% zakupu ex-factory dla brandu "${brand.name}" nie może być ujemna.`,
				);
				return;
			}

			const thresholdPercents = new Set<number>();
			for (const tier of brand.settlementTiers ?? []) {
				if (tier.thresholdPercent <= 0) {
					toast.error(`Próg realizacji dla brandu "${brand.name}" musi być większy od zera.`);
					return;
				}
				if (thresholdPercents.has(tier.thresholdPercent)) {
					toast.error(`Brand "${brand.name}" ma zdublowany próg ${tier.thresholdPercent}%.`);
					return;
				}
				thresholdPercents.add(tier.thresholdPercent);
				if (tier.settlementPercent == null && tier.settlementAmount == null) {
					toast.error(`Brand "${brand.name}" musi mieć uzupełnione rozliczenie (%) lub (zł).`);
					return;
				}
				if ((tier.settlementPercent ?? 0) < 0) {
					toast.error(`Rozliczenie procentowe dla brandu "${brand.name}" nie może być ujemne.`);
					return;
				}
				if ((tier.settlementAmount ?? 0) < 0) {
					toast.error(`Rozliczenie kwotowe dla brandu "${brand.name}" nie może być ujemne.`);
					return;
				}
			}
		}
		const brandBasketNames = new Map<string, string>();
		for (const basket of baskets) {
			for (const product of basket.products) {
				if (!product.brandClientId) continue;
				const currentBasket = brandBasketNames.get(product.brandClientId);
				if (currentBasket && currentBasket !== basket.name) {
					toast.error(
						`Ten sam brand nie może występować w koszykach "${currentBasket}" i "${basket.name}".`,
					);
					return;
				}
				brandBasketNames.set(product.brandClientId, basket.name);
			}
		}
		if (!isValidPeriodCode(quarter)) {
			toast.error("Wybierz prawidłowy okres kontraktu.");
			return;
		}
		const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
		for (const p of pharmacies) {
			if (!p.startDate || !ISO_DATE.test(p.startDate)) {
				toast.error(
					`Apteka "${p.unitname}" (${p.unitcode}) ma nieprawidłową datę rozpoczęcia — uzupełnij pole "Od".`,
				);
				return;
			}
			// Backend przyjmuje end_date jako NaiveDate (nie Option) — pusta albo
			// odwrocona data wraca stamtad jako 422 bez wskazania apteki.
			if (!p.endDate || !ISO_DATE.test(p.endDate)) {
				toast.error(
					`Apteka "${p.unitname}" (${p.unitcode}) ma nieprawidłową datę zakończenia — uzupełnij pole "Do".`,
				);
				return;
			}
			if (p.endDate < p.startDate) {
				toast.error(
					`Apteka "${p.unitname}" (${p.unitcode}) ma datę zakończenia wcześniejszą niż rozpoczęcia.`,
				);
				return;
			}
		}
		saveMutation.mutate({
			name,
			producerId,
			year,
			quarter,
			pricingMode,
			ownerUserId,
			minTotalSalesValue: parseOptionalNumber(minTotalSalesValue) ?? null,
			minTotalPurchaseValue: parseOptionalNumber(minTotalPurchaseValue) ?? null,
			minTotalSalesQuantity: parseOptionalNumber(minTotalSalesQuantity) ?? null,
			minTotalPurchaseQuantity: parseOptionalNumber(minTotalPurchaseQuantity) ?? null,
			marketingGuaranteed: parseOptionalNumber(marketingGuaranteed) ?? null,
			exposureRealized: exposureRealized === "" ? null : exposureRealized === "true",
			exposureDecidedAt: exposureDecidedAt === "" ? null : exposureDecidedAt,
			exposureNote: exposureNote.trim() === "" ? null : exposureNote.trim(),
			sumSalesPercentsOnPurchase,
			contractTiers: contractTiers.map((t) => ({
				salesFrom: t.salesFrom,
				salesTo: t.salesTo,
				purchaseFrom: t.purchaseFrom,
				purchaseTo: t.purchaseTo,
				salesPercent: t.salesPercent,
				purchasePercent: t.purchasePercent,
			})),
			pharmacies: pharmacies.map((p) => ({
				unitcode: p.unitcode,
				startDate: p.startDate,
				endDate: p.endDate,
			})),
			brands: brands.map(
				({
					tempId,
					name,
					minPurchaseExfactoryValue,
					purchaseValue100Exfactory,
					settlementTiers,
				}) => ({
					clientId: tempId,
					name: name.trim(),
					minPurchaseExfactoryValue,
					purchaseValue100Exfactory,
					settlementTiers: (settlementTiers ?? [])
						.slice()
						.sort((a, b) => a.thresholdPercent - b.thresholdPercent)
						.map(({ tempId: _tierTempId, tierId: _tierId, ...tier }) => tier),
				}),
			),
			productSurcharges: productSurcharges.map(
				({ name: _name, tempId: _tempId, surchargeId: _surchargeId, ...rest }) => rest,
			),
			mslItems: mslItems.map(({ name: _name, tempId: _tempId, itemId: _itemId, ...rest }) => ({
				...rest,
				procentWymagalnosci: mslWymagalnoscTryb === "total" ? undefined : rest.procentWymagalnosci,
			})),
			mslWymagalnoscTryb,
			mslProcentWymagalnosciGlobal:
				mslWymagalnoscTryb === "total"
					? (parseOptionalNumber(mslProcentWymagalnosciGlobal) ?? null)
					: null,
			baskets: baskets.map(({ tempId, products, settlements, ...rest }) => ({
				...rest,
				products: products.map((p) => ({
					bloz07: p.bloz07,
					brandClientId: p.brandClientId,
				})),
				settlements: settlements.map(({ tempId: _, tiers, ...sRest }) => ({
					...sRest,
					// Kolumny NOT NULL (sales_from, purchase_from, sales_percent, purchase_percent):
					// puste pole -> NaN -> null w JSON -> 500 przy zapisie. Koercja NaN -> 0.
					// (sales_to/purchase_to sa nullable — "do" = nieskonczonosc, zostawiamy.)
					tiers: tiers.map(({ tempId: __, ...tRest }) => ({
						...tRest,
						salesFrom: Number.isFinite(tRest.salesFrom) ? tRest.salesFrom : 0,
						purchaseFrom: Number.isFinite(tRest.purchaseFrom) ? tRest.purchaseFrom : 0,
						salesPercent: Number.isFinite(tRest.salesPercent) ? tRest.salesPercent : 0,
						purchasePercent: Number.isFinite(tRest.purchasePercent) ? tRest.purchasePercent : 0,
					})),
				})),
			})),
			exfactoryPriceListIds: isEdit ? undefined : createExfactoryIds,
		});
	};

	const calculatePercent = (value: number, total?: number) => {
		if (!total || total === 0) return "0.00%";
		return `${((value / total) * 100).toFixed(2)}%`;
	};

	const ownerSelectValue = ownerUserId == null ? "" : String(ownerUserId);

	const ownerLabel = (candidate: ContractOwnerCandidate) => candidate.name;

	const ownerOptions = useMemo(() => {
		if (
			contract?.ownerUserId &&
			!ownerCandidates.some((candidate) => candidate.id === contract.ownerUserId)
		) {
			return [
				{
					id: contract.ownerUserId,
					name: contract.ownerName || "Nieznany użytkownik",
					email: contract.ownerEmail || "",
				},
				...ownerCandidates,
			];
		}

		return ownerCandidates;
	}, [contract, ownerCandidates]);

	const getBrandOptionsForBasket = (basketIndex: number) => {
		const usedInOtherBaskets = new Set<string>();
		baskets.forEach((basket, index) => {
			if (index === basketIndex) return;
			basket.products.forEach((product) => {
				if (product.brandClientId) usedInOtherBaskets.add(product.brandClientId);
			});
		});
		return brands.filter((brand) => !usedInOtherBaskets.has(brand.tempId));
	};

	return (
		<div className="space-y-6 p-6 pb-28 max-md:space-y-4 max-md:px-3 max-md:pt-3 max-md:pb-0">
			<PageHeader
				title={isEdit ? "Edytuj Kontrakt" : "Nowy Kontrakt"}
				description="Zdefiniuj warunki kontraktu i koszyki produktów"
			/>

			<form id={formId} onSubmit={handleSubmit} className="space-y-8 max-md:space-y-6">
				{/* ─── DANE PODSTAWOWE ─── */}
				<div className="bg-card p-6 rounded-lg border border-border shadow-sm space-y-6 max-md:p-3">
					<div className="space-y-4">
						<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
							Dane podstawowe
						</h3>
						{/* Linia 1: identyfikacja (5 pol) */}
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
							<div className="space-y-2">
								<label htmlFor={`${formId}-name`} className="text-sm font-medium">
									Nazwa kontraktu
								</label>
								<Input
									id={`${formId}-name`}
									required
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="np. Kontrakt ADAMED 2026"
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-producer`} className="text-sm font-medium">
									Producent
								</label>
								<ProducerSelector
									id={`${formId}-producer`}
									producers={producers}
									value={producerId}
									onChange={setProducerId}
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-year`} className="text-sm font-medium">
									Rok
								</label>
								<Input
									id={`${formId}-year`}
									type="number"
									required
									min={2000}
									max={2100}
									value={yearInput}
									onChange={(e) => setYearInput(e.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-quarter`} className="text-sm font-medium">
									Okres
								</label>
								<select
									id={`${formId}-quarter`}
									required
									className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
									value={quarter}
									onChange={(e) => setQuarter(e.target.value)}
								>
									<optgroup label="Kwartał (3 mies.)">
										{PERIOD_OPTIONS.filter((p) => p.kind === "quarter").map((p) => (
											<option key={p.code} value={p.code}>
												{p.label}
											</option>
										))}
									</optgroup>
									<optgroup label="Okres 4-mies.">
										{PERIOD_OPTIONS.filter((p) => p.kind === "tertial").map((p) => (
											<option key={p.code} value={p.code}>
												{p.label}
											</option>
										))}
									</optgroup>
									<optgroup label="Półrocze (6 mies.)">
										{PERIOD_OPTIONS.filter((p) => p.kind === "half").map((p) => (
											<option key={p.code} value={p.code}>
												{p.label}
											</option>
										))}
									</optgroup>
								</select>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-owner`} className="text-sm font-medium">
									Opiekun
								</label>
								<select
									id={`${formId}-owner`}
									className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
									value={ownerSelectValue}
									onChange={(e) =>
										setOwnerUserId(e.target.value === "" ? null : Number(e.target.value))
									}
								>
									<option value="">Brak opiekuna</option>
									{ownerOptions.map((candidate) => (
										<option key={candidate.id} value={candidate.id}>
											{ownerLabel(candidate)}
										</option>
									))}
								</select>
							</div>
						</div>

						{/* Linia 2: progi minimalne + marketing (5 pol) */}
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
							<div className="space-y-2">
								<label htmlFor={`${formId}-min-sales`} className="text-sm font-medium">
									Minimalna wartość sprzedaży
								</label>
								<Input
									id={`${formId}-min-sales`}
									type="number"
									min={0}
									step="0.01"
									value={minTotalSalesValue}
									onChange={(e) => setMinTotalSalesValue(e.target.value)}
									placeholder="np. 100000"
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-min-purchase`} className="text-sm font-medium">
									Minimalna wartość zakupu
								</label>
								<Input
									id={`${formId}-min-purchase`}
									type="number"
									min={0}
									step="0.01"
									value={minTotalPurchaseValue}
									onChange={(e) => setMinTotalPurchaseValue(e.target.value)}
									placeholder="np. 80000"
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-min-sales-qty`} className="text-sm font-medium">
									Minimalna ilość sprzedaży
								</label>
								<Input
									id={`${formId}-min-sales-qty`}
									type="number"
									min={0}
									step="1"
									value={minTotalSalesQuantity}
									onChange={(e) => setMinTotalSalesQuantity(e.target.value)}
									placeholder="np. 500"
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-min-purchase-qty`} className="text-sm font-medium">
									Minimalna ilość zakupu
								</label>
								<Input
									id={`${formId}-min-purchase-qty`}
									type="number"
									min={0}
									step="1"
									value={minTotalPurchaseQuantity}
									onChange={(e) => setMinTotalPurchaseQuantity(e.target.value)}
									placeholder="np. 400"
								/>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-marketing-guaranteed`} className="text-sm font-medium">
									Marketing gwarantowany
								</label>
								<Input
									id={`${formId}-marketing-guaranteed`}
									type="number"
									min={0}
									step="0.01"
									value={marketingGuaranteed}
									onChange={(e) => setMarketingGuaranteed(e.target.value)}
									placeholder="np. 5000"
								/>
							</div>
						</div>

						{/* Linia 3: progi całego kontraktu (Zakup + Sprzedaż) */}
						<div className="space-y-2 rounded-md border border-border bg-background/50 p-3">
							<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
								<span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
									Progi całego kontraktu (Zakup + Sprzedaż)
								</span>
								<Button
									type="button"
									variant="ghost"
									className="h-6 px-2 text-[9px] font-bold hover:bg-primary/10 hover:text-primary max-md:h-9 max-md:text-xs"
									onClick={addContractTier}
								>
									<Plus className="mr-1 size-3" /> Dodaj wiersz progu
								</Button>
							</div>
							<p className="text-[11px] text-muted-foreground">
								Progi liczone od całkowitej realizacji kontraktu (suma wszystkich koszy). Dają
								osobne pozycje w bloku „Wynagrodzenie" na stronie rozliczeń, niezależnie od progów
								koszy.
							</p>

							{contractTiers.length > 0 && (
								<div className="grid grid-cols-[2fr_2fr_1.4fr_2fr_2fr_1.4fr_auto] gap-2 px-1 max-md:hidden">
									<div className="col-span-3 border-b border-orange-500/20 pb-1 text-center text-[8px] font-bold uppercase text-orange-500/80">
										Zakup
									</div>
									<div className="col-span-3 border-b border-blue-500/20 pb-1 text-center text-[8px] font-bold uppercase text-blue-500/80">
										Sprzedaż
									</div>
									<div />
								</div>
							)}

							{contractTiers.map((t) => (
								<div
									key={t.tempId}
									className="grid grid-cols-[2fr_2fr_1.4fr_2fr_2fr_1.4fr_auto] items-end gap-2 rounded border border-border/40 bg-background p-2 shadow-sm max-md:grid-cols-3"
								>
									<div className="space-y-1">
										<span className="text-[8px] font-bold uppercase text-orange-600/70 opacity-50">
											Zak. Od
										</span>
										<Input
											type="number"
											step="0.01"
											className="h-7 p-1 text-[10px] max-md:h-9 max-md:text-sm"
											value={Number.isFinite(t.purchaseFrom) ? t.purchaseFrom : ""}
											onChange={(e) =>
												updateContractTier(t.tempId, { purchaseFrom: parseFloat(e.target.value) })
											}
										/>
									</div>
									<div className="space-y-1">
										<span className="text-[8px] font-bold uppercase text-orange-600/70 opacity-50">
											Zak. Do
										</span>
										<Input
											type="number"
											step="0.01"
											className="h-7 p-1 text-[10px] max-md:h-9 max-md:text-sm"
											placeholder="∞"
											value={t.purchaseTo || ""}
											onChange={(e) =>
												updateContractTier(t.tempId, {
													purchaseTo: parseOptionalNumber(e.target.value),
												})
											}
										/>
									</div>
									<div className="space-y-1">
										<span className="text-[8px] font-bold uppercase text-orange-600/70 opacity-50">
											Zak. %
										</span>
										<Input
											type="number"
											step="0.01"
											className="h-7 p-1 text-[10px] font-bold text-orange-600 max-md:h-9 max-md:text-sm"
											value={Number.isFinite(t.purchasePercent) ? t.purchasePercent : ""}
											onChange={(e) =>
												updateContractTier(t.tempId, {
													purchasePercent: parseFloat(e.target.value),
												})
											}
										/>
									</div>
									<div className="space-y-1">
										<span className="text-[8px] font-bold uppercase opacity-50">Sprz. Od</span>
										<Input
											type="number"
											step="0.01"
											className="h-7 p-1 text-[10px] max-md:h-9 max-md:text-sm"
											value={Number.isFinite(t.salesFrom) ? t.salesFrom : ""}
											onChange={(e) =>
												updateContractTier(t.tempId, { salesFrom: parseFloat(e.target.value) })
											}
										/>
									</div>
									<div className="space-y-1">
										<span className="text-[8px] font-bold uppercase opacity-50">Sprz. Do</span>
										<Input
											type="number"
											step="0.01"
											className="h-7 p-1 text-[10px] max-md:h-9 max-md:text-sm"
											placeholder="∞"
											value={t.salesTo || ""}
											onChange={(e) =>
												updateContractTier(t.tempId, {
													salesTo: parseOptionalNumber(e.target.value),
												})
											}
										/>
									</div>
									<div className="space-y-1">
										<span className="text-[8px] font-bold uppercase text-blue-600/70 opacity-50">
											Sprz. %
										</span>
										<Input
											type="number"
											step="0.01"
											className="h-7 p-1 text-[10px] font-bold text-blue-600 max-md:h-9 max-md:text-sm"
											value={Number.isFinite(t.salesPercent) ? t.salesPercent : ""}
											onChange={(e) =>
												updateContractTier(t.tempId, { salesPercent: parseFloat(e.target.value) })
											}
										/>
									</div>
									<button
										type="button"
										onClick={() => removeContractTier(t.tempId)}
										className="flex size-7 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive max-md:col-start-3 max-md:size-9 max-md:justify-self-end"
									>
										<Trash2 className="size-3.5" />
									</button>
								</div>
							))}
						</div>

						{/* Linia 4: tryb wynagrodzenia sprzedazowego (blok "Wynagrodzenie" na rozliczeniach) */}
						<label
							htmlFor={`${formId}-sum-sales-percents`}
							className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-muted/20 p-4"
						>
							<input
								id={`${formId}-sum-sales-percents`}
								type="checkbox"
								className="mt-0.5 size-4 accent-primary"
								checked={sumSalesPercentsOnPurchase}
								onChange={(e) => setSumSalesPercentsOnPurchase(e.target.checked)}
							/>
							<span className="space-y-1">
								<span className="block text-sm font-medium">
									Sumuj procenty SPRZEDAŻ z zaliczonych koszy od całej realizacji ZAKUPU do
									wynagrodzenia (Kontrakt POLFARMEX)
								</span>
								<span className="block text-xs text-muted-foreground">
									Gdy włączone, w bloku „Wynagrodzenie" na stronie rozliczeń procenty progów
									sprzedaży z koszy, które osiągnęły próg, są sumowane i mnożone przez całą
									realizację zakupu kontraktu — zamiast liczenia progu sprzedaży osobno dla każdego
									koszyka.
								</span>
							</span>
						</label>
					</div>

					{/* ─── REALIZACJA / DECYZJA PRODUCENTA ─── */}
					<div className="bg-card p-6 rounded-lg border border-border shadow-sm max-md:p-3">
						<div className="space-y-4">
							<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
								Realizacja / decyzja producenta
							</h3>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
								<div className="space-y-2">
									<label htmlFor={`${formId}-exposure-realized`} className="text-sm font-medium">
										MERCH - ekspozycja
									</label>
									<select
										id={`${formId}-exposure-realized`}
										className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
										value={exposureRealized}
										onChange={(e) => setExposureRealized(e.target.value)}
									>
										<option value="">— Brak decyzji</option>
										<option value="true">Tak — ekspozycja zaliczona</option>
										<option value="false">Nie — ekspozycja niezaliczona</option>
									</select>
								</div>
								<div className="space-y-2">
									<label htmlFor={`${formId}-exposure-decided-at`} className="text-sm font-medium">
										Data decyzji
									</label>
									<Input
										id={`${formId}-exposure-decided-at`}
										type="date"
										value={exposureDecidedAt}
										onChange={(e) => setExposureDecidedAt(e.target.value)}
									/>
								</div>
							</div>
							<div className="space-y-2">
								<label htmlFor={`${formId}-exposure-note`} className="text-sm font-medium">
									Uzasadnienie producenta
								</label>
								<textarea
									id={`${formId}-exposure-note`}
									rows={3}
									className="flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
									value={exposureNote}
									onChange={(e) => setExposureNote(e.target.value)}
									placeholder="Opcjonalnie — komentarz producenta do decyzji o ekspozycji"
								/>
							</div>
						</div>
					</div>

					{/* ─── CENNIKI EXFACTORY ─── */}
					{isEdit ? (
						<ExFactoryPriceListsSection
							mode="edit"
							contractId={Number(id)}
							producerId={producerId}
							onPendingChange={(saveFn) => {
								pendingExfactorySaveRef.current = saveFn;
							}}
						/>
					) : (
						<ExFactoryPriceListsSection
							mode="create"
							producerId={producerId}
							year={year}
							quarter={quarter}
							selectedIds={createExfactoryIds}
							onSelectedIdsChange={setCreateExfactoryIds}
						/>
					)}

					{/* ─── APTEKI KONTRAKTU ─── */}
					<div className="space-y-4 pt-6 border-t">
						<div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-2">
							<div className="flex items-center gap-4 max-md:flex-wrap max-md:gap-2">
								<button
									type="button"
									onClick={() => setIsPharmaciesExpanded(!isPharmaciesExpanded)}
									className="flex items-center gap-2 hover:opacity-80 transition-opacity max-md:min-h-9"
								>
									{isPharmaciesExpanded ? (
										<ChevronUp className="size-4 text-muted-foreground" />
									) : (
										<ChevronDown className="size-4 text-muted-foreground" />
									)}
									<Store className="size-4 text-primary" />
									<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
										Apteki objęte kontraktem ({pharmacies.length})
									</h3>
								</button>
								{isPharmaciesExpanded && pharmacies.length > 5 && (
									<div className="relative w-64 max-md:w-full">
										<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground" />
										<Input
											placeholder="Szukaj w wybranych..."
											value={pharmacySearchQuery}
											onChange={(e) => setPharmacySearchQuery(e.target.value)}
											className="h-7 pl-8 text-[10px] bg-muted/20 max-md:h-9 max-md:text-sm"
										/>
									</div>
								)}
							</div>
							<div className="flex items-center gap-2 max-md:flex-wrap max-md:[&_button]:h-9 max-md:[&>div>div]:h-9">
								{pharmacies.length > 0 && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={exportPharmacies}
										className="gap-2 h-8 text-xs"
									>
										<Download className="size-4" /> Eksport
									</Button>
								)}
								<div className="flex items-center">
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => document.getElementById("contract-pharmacy-import")?.click()}
										className="gap-2 h-8 text-xs rounded-r-none border-r-0"
									>
										<FileUp className="size-4" /> Importuj
										<input
											id="contract-pharmacy-import"
											type="file"
											className="hidden"
											accept=".xls,.xlsx"
											onChange={handlePharmacyImport}
										/>
									</Button>
									<TooltipProvider>
										<Tooltip delayDuration={300}>
											<TooltipTrigger asChild>
												<div
													title="Instrukcja importu aptek"
													className="flex h-8 items-center justify-center rounded-r-md border border-input bg-background px-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-help transition-colors"
												>
													<Info className="size-4" />
												</div>
											</TooltipTrigger>
											<TooltipContent className="max-w-sm space-y-2 p-3 text-xs" side="bottom">
												<p className="font-bold">Instrukcja importu aptek:</p>
												<ul className="list-disc pl-4 space-y-1">
													<li>
														Akceptowane pliki: <span className="font-mono">.xlsx, .xls</span>.
														Pierwszy wiersz to nagłówek.
													</li>
													<li>
														Wymagana kolumna <span className="font-bold text-primary">KSID</span>{" "}
														(albo <span className="font-mono">unitcode</span>,{" "}
														<span className="font-mono">kod apteki</span>) — 5 lub 6 cyfr, bez
														dopisywania wiodącego zera.
													</li>
													<li>
														Kolumny <span className="font-bold text-primary">Od</span> i{" "}
														<span className="font-bold text-primary">Do</span> są opcjonalne.
														Formaty: <span className="font-mono">rrrr-mm-dd</span>,{" "}
														<span className="font-mono">dd.mm.rrrr</span> lub komórka daty Excela.
													</li>
													<li>
														Bez kolumn Od/Do (albo gdy data jest nieczytelna) apteka dostaje zakres
														okresu kontraktu:{" "}
														<span className="font-mono whitespace-nowrap">
															{pharmacyDefaultRange.startDate} … {pharmacyDefaultRange.endDate}
														</span>
														. Daty można potem poprawić w tabeli.
													</li>
													<li>
														Import dokłada apteki do już wybranych; powtórzony KSID nadpisuje daty,
														nie duplikuje wiersza.
													</li>
													<li>
														Kody nieznane w FM2 lub o złym formacie trafiają na listę „nie
														zaczytane” nad tabelą — z możliwością skopiowania.
													</li>
												</ul>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</div>
								{pharmacies.length > 0 && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => setPharmacies([])}
										className="gap-2 h-8 text-xs"
									>
										<Trash2 className="size-4" /> Usuń wszystkie
									</Button>
								)}
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => setIsPharmacyModalOpen(true)}
									className="gap-2 h-8 text-xs"
								>
									<Plus className="size-4" /> Wybierz apteki
								</Button>
							</div>
						</div>

						{pharmacyImportReport && (
							<ImportReportPanel
								report={pharmacyImportReport}
								format={KSID_FORMAT}
								onDismiss={() => setPharmacyImportReport(null)}
								className="mb-3"
							/>
						)}

						{/* Pola dat trzymamy przy PRAWEJ krawedzi, zeby staly pod kolumnami
						    "Od"/"Do" tabeli — wtedy widac, ktore wartosci zmienia przycisk.
						    Po lewej zostaje sam kontekst: zakres okresu kontraktu. */}
						{isPharmaciesExpanded && pharmacies.length > 0 && (
							<div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 max-md:[&_button]:h-9 max-md:[&_input]:h-9">
								<CalendarRange className="size-4 text-muted-foreground" />
								<span className="text-[11px] text-muted-foreground">
									Okres kontraktu:{" "}
									<span className="font-mono whitespace-nowrap">
										{pharmacyDefaultRange.startDate} … {pharmacyDefaultRange.endDate}
									</span>
								</span>
								{isPharmacyRangeCustom && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => {
											setBulkStartDate(pharmacyDefaultRange.startDate);
											setBulkEndDate(pharmacyDefaultRange.endDate);
										}}
										className="h-8 text-xs text-muted-foreground"
									>
										Przywróć okres kontraktu
									</Button>
								)}
								<div className="ml-auto flex flex-wrap items-center gap-2">
									<span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
										Daty dla wszystkich
									</span>
									<Input
										type="date"
										aria-label="Data od dla wszystkich aptek"
										value={bulkStartDate}
										onChange={(e) => setBulkStartDate(e.target.value)}
										className="h-8 w-36 text-[11px]"
									/>
									<span className="text-muted-foreground">—</span>
									<Input
										type="date"
										aria-label="Data do dla wszystkich aptek"
										value={bulkEndDate}
										onChange={(e) => setBulkEndDate(e.target.value)}
										className="h-8 w-36 text-[11px]"
									/>
									<Button
										type="button"
										size="sm"
										onClick={applyBulkPharmacyDates}
										className="h-8 text-xs max-md:h-9"
									>
										{pharmacySearchQuery
											? `Zastosuj do ${filteredSelectedPharmacies.length} widocznych`
											: `Zastosuj do wszystkich (${pharmacies.length})`}
									</Button>
								</div>
							</div>
						)}

						{isPharmaciesExpanded && (
							<div className="animate-in fade-in slide-in-from-top-1 duration-200">
								<div className="border border-border rounded-lg overflow-hidden bg-card shadow-sm max-md:overflow-x-auto">
									<table className="w-full text-xs border-collapse max-md:min-w-[46rem]">
										<thead>
											<tr className="bg-muted/50 border-b border-border">
												<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter">
													Nazwa apteki
												</th>
												<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter w-40">
													KSID
												</th>
												<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter w-60">
													Region / Grupa
												</th>
												<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter w-40">
													Od
												</th>
												<th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase tracking-tighter w-40">
													Do
												</th>
												<th className="px-4 py-2 w-10" />
											</tr>
										</thead>
										<tbody className="divide-y divide-border/40">
											{filteredSelectedPharmacies.map((p) => (
												<tr key={p.unitcode} className="hover:bg-muted/20 transition-colors">
													<td className="px-4 py-2 font-medium">{p.unitname}</td>
													<td className="px-4 py-2 font-mono font-black text-primary">
														{p.unitcode}
													</td>
													<td className="px-4 py-2">
														{p.region ? (
															<Badge className="text-[9px] h-5 font-bold uppercase tracking-tighter">
																{p.region}
															</Badge>
														) : (
															<span className="text-muted-foreground italic opacity-50">
																Brak danych
															</span>
														)}
													</td>
													<td className="px-4 py-2">
														<Input
															type="date"
															value={p.startDate}
															onChange={(e) =>
																updatePharmacyDate(p.unitcode, "startDate", e.target.value)
															}
															className="h-8 text-[11px]"
														/>
													</td>
													<td className="px-4 py-2">
														<Input
															type="date"
															value={p.endDate}
															onChange={(e) =>
																updatePharmacyDate(p.unitcode, "endDate", e.target.value)
															}
															className="h-8 text-[11px]"
														/>
													</td>
													<td className="px-4 py-2 text-right">
														<button
															type="button"
															onClick={() => removePharmacy(p.unitcode)}
															className="p-1 text-muted-foreground hover:text-status-error-text transition-colors max-md:p-2.5"
														>
															<X className="size-4" />
														</button>
													</td>
												</tr>
											))}
											{pharmacies.length === 0 && (
												<tr>
													<td
														colSpan={6}
														className="py-12 text-center text-muted-foreground italic"
													>
														Nie wybrano żadnych aptek. Kontrakt będzie dotyczył wszystkich aptek
														domyślnie.
													</td>
												</tr>
											)}
											{pharmacies.length > 0 && filteredSelectedPharmacies.length === 0 && (
												<tr>
													<td colSpan={6} className="py-8 text-center text-muted-foreground italic">
														Brak wyników dla frazy "{pharmacySearchQuery}"
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="space-y-4">
					<div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-2">
						<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
							Dopłaty do produktów
						</h3>
						<div className="flex items-center gap-2 max-md:flex-wrap">
							<div className="flex items-center">
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-9 rounded-r-none border-r-0"
									onClick={() => document.getElementById("surcharge-import")?.click()}
								>
									<FileUp className="mr-2 size-4" />
									Importuj dopłaty
									<input
										id="surcharge-import"
										type="file"
										className="hidden"
										accept=".xls,.xlsx"
										onChange={handleSurchargeImport}
									/>
								</Button>
								<TooltipProvider>
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<div className="flex h-9 items-center justify-center rounded-r-md border border-input bg-background px-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-help transition-colors">
												<Info className="size-4" />
											</div>
										</TooltipTrigger>
										<TooltipContent className="max-w-xs space-y-2 p-3 text-xs" side="bottom">
											<p className="font-bold">Instrukcja importu dopłat:</p>
											<ul className="list-disc pl-4 space-y-1">
												<li>
													Akceptowane pliki: <span className="font-mono">.xlsx, .xls</span>
												</li>
												<li>
													Wymagane 5 kolumn w tej kolejności:
													<ol className="list-decimal pl-4 mt-1">
														<li>
															<span className="font-bold text-primary">BLOZ</span> — kod produktu (7
															cyfr)
														</li>
														<li>
															<span className="font-bold text-primary">Sell-in %</span>
														</li>
														<li>
															<span className="font-bold text-primary">Sell-in zł</span>
														</li>
														<li>
															<span className="font-bold text-primary">Sell-out %</span>
														</li>
														<li>
															<span className="font-bold text-primary">Sell-out zł</span>
														</li>
													</ol>
												</li>
												<li>Pierwszy wiersz to nagłówek (pomijany).</li>
												<li>Puste komórki dopłat zostają puste; zł obsługuje przecinek.</li>
											</ul>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="h-9 gap-2"
								onClick={exportSurcharges}
								disabled={productSurcharges.length === 0}
							>
								<Download className="size-4" />
								Eksport
							</Button>
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="h-9"
								onClick={() => {
									setProductSelectionTarget({ kind: "surcharge" });
									setIsProductModalOpen(true);
								}}
							>
								<Plus className="mr-2 size-4" />
								Dodaj produkt
							</Button>
						</div>
					</div>

					{productSurcharges.length === 0 ? (
						<div className="rounded-lg border border-dashed border-border bg-muted/30 py-8 text-center">
							<p className="text-sm text-muted-foreground">
								Brak zdefiniowanych dopłat do produktów.
							</p>
						</div>
					) : (
						<div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm max-md:overflow-x-auto">
							<table className="w-full border-collapse text-xs max-md:min-w-[44rem]">
								<thead>
									<tr className="border-b border-border bg-muted/40">
										<th className="px-4 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Produkt
										</th>
										<th className="w-40 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Sell-in %
										</th>
										<th className="w-40 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Sell-in zł
										</th>
										<th className="w-40 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Sell-out %
										</th>
										<th className="w-40 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Sell-out zł
										</th>
										<th className="w-12 px-3 py-2" />
									</tr>
								</thead>
								<tbody className="divide-y divide-border/40">
									{productSurcharges.map((item) => (
										<tr key={item.tempId} className="align-top">
											<td className="px-4 py-3">
												<div className="space-y-1">
													<div className="text-[10px] font-bold uppercase leading-none text-primary">
														{item.bloz07}
													</div>
													<div className="text-sm font-medium leading-tight">{item.name}</div>
												</div>
											</td>
											<td className="px-3 py-3">
												<Input
													type="number"
													step="0.01"
													placeholder="0.00"
													value={item.doplataSellInProcent ?? ""}
													onChange={(e) =>
														updateProductSurcharge(item.tempId, {
															doplataSellInProcent: parseOptionalNumber(e.target.value),
														})
													}
												/>
											</td>
											<td className="px-3 py-3">
												<Input
													type="number"
													step="0.01"
													placeholder="0.00"
													value={item.doplataSellInKwota ?? ""}
													onChange={(e) =>
														updateProductSurcharge(item.tempId, {
															doplataSellInKwota: parseOptionalNumber(e.target.value),
														})
													}
												/>
											</td>
											<td className="px-3 py-3">
												<Input
													type="number"
													step="0.01"
													placeholder="0.00"
													value={item.doplataSellOutProcent ?? ""}
													onChange={(e) =>
														updateProductSurcharge(item.tempId, {
															doplataSellOutProcent: parseOptionalNumber(e.target.value),
														})
													}
												/>
											</td>
											<td className="px-3 py-3">
												<Input
													type="number"
													step="0.01"
													placeholder="0.00"
													value={item.doplataSellOutKwota ?? ""}
													onChange={(e) =>
														updateProductSurcharge(item.tempId, {
															doplataSellOutKwota: parseOptionalNumber(e.target.value),
														})
													}
												/>
											</td>
											<td className="px-3 py-3 text-right">
												<button
													type="button"
													className="text-muted-foreground transition-colors hover:text-status-error-text max-md:inline-flex max-md:size-9 max-md:items-center max-md:justify-center"
													onClick={() => removeProductSurcharge(item.tempId)}
												>
													<X className="size-4" />
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{/* ─── MSL - DOSTĘPNOŚĆ ─── */}
				<div className="space-y-4">
					<div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-2">
						<div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-2">
							<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
								MSL — dostępność ({mslItems.length})
							</h3>
							{/* Segment control: tryb % wymagalności */}
							<div className="flex rounded-md border border-input text-xs overflow-hidden max-md:[&>button]:min-h-9">
								<button
									type="button"
									onClick={() => setMslWymagalnoscTryb("per_produkt")}
									className={`px-2.5 py-1 transition-colors ${
										mslWymagalnoscTryb === "per_produkt"
											? "bg-primary text-primary-foreground"
											: "bg-background text-muted-foreground hover:bg-muted"
									}`}
								>
									% per produkt
								</button>
								<button
									type="button"
									onClick={() => setMslWymagalnoscTryb("total")}
									className={`px-2.5 py-1 border-l border-input transition-colors ${
										mslWymagalnoscTryb === "total"
											? "bg-primary text-primary-foreground"
											: "bg-background text-muted-foreground hover:bg-muted"
									}`}
								>
									% całościowo
								</button>
							</div>
							{mslWymagalnoscTryb === "total" && (
								<div className="flex items-center gap-1.5">
									<label
										htmlFor="msl-procent-global"
										className="text-xs text-muted-foreground whitespace-nowrap"
									>
										% wymagalności:
									</label>
									<Input
										id="msl-procent-global"
										type="number"
										step="0.01"
										min="0"
										max="100"
										placeholder="np. 80.00"
										value={mslProcentWymagalnosciGlobal}
										onChange={(e) => setMslProcentWymagalnosciGlobal(e.target.value)}
										className="h-7 w-28 text-xs max-md:h-9"
									/>
								</div>
							)}
						</div>
						<div className="flex items-center gap-2 max-md:flex-wrap">
							<div className="flex items-center">
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-9 rounded-r-none border-r-0"
									onClick={() => document.getElementById("msl-import")?.click()}
								>
									<FileUp className="mr-2 size-4" />
									Importuj MSL
									<input
										id="msl-import"
										type="file"
										className="hidden"
										accept=".xls,.xlsx"
										onChange={handleMslImport}
									/>
								</Button>
								<TooltipProvider>
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<div className="flex h-9 items-center justify-center rounded-r-md border border-input bg-background px-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-help transition-colors">
												<Info className="size-4" />
											</div>
										</TooltipTrigger>
										<TooltipContent className="max-w-xs space-y-2 p-3 text-xs" side="bottom">
											<p className="font-bold">Instrukcja importu MSL:</p>
											<ul className="list-disc pl-4 space-y-1">
												<li>
													Akceptowane pliki: <span className="font-mono">.xlsx, .xls</span>
												</li>
												<li>
													4 kolumny w tej kolejności:
													<ol className="list-decimal pl-4 mt-1">
														<li>
															<span className="font-bold text-muted-foreground">Nazwa</span> —
															opcjonalna, pomijana przy imporcie
														</li>
														<li>
															<span className="font-bold text-primary">BLOZ</span> — kod produktu (7
															cyfr)
														</li>
														<li>
															<span className="font-bold text-primary">MSL dostępność (szt.)</span>
														</li>
														<li>
															<span className="font-bold text-primary">% wymagalności</span>
														</li>
													</ol>
												</li>
												<li>Pierwszy wiersz to nagłówek (pomijany).</li>
												<li>Produkty już na liście są pomijane.</li>
											</ul>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="h-9 gap-2"
								onClick={exportMslItems}
								disabled={mslItems.length === 0}
							>
								<Download className="size-4" />
								Eksport
							</Button>
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="h-9"
								onClick={() => {
									setProductSelectionTarget({ kind: "msl" });
									setIsProductModalOpen(true);
								}}
							>
								<Plus className="mr-2 size-4" />
								Dodaj produkt
							</Button>
						</div>
					</div>

					{mslItems.length === 0 ? (
						<div className="rounded-lg border border-dashed border-border bg-muted/30 py-8 text-center">
							<p className="text-sm text-muted-foreground">Brak zdefiniowanych pozycji MSL.</p>
						</div>
					) : (
						<div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm max-md:overflow-x-auto">
							<table className="w-full border-collapse text-xs max-md:min-w-[44rem]">
								<thead>
									<tr className="border-b border-border bg-muted/40">
										<th className="px-4 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Produkt
										</th>
										<th className="w-48 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											MSL dostępność (szt.)
										</th>
										{mslWymagalnoscTryb === "per_produkt" && (
											<th className="w-48 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
												% wymagalności
											</th>
										)}
										<th className="w-12 px-3 py-2" />
									</tr>
								</thead>
								<tbody className="divide-y divide-border/40">
									{mslItems.map((item) => (
										<tr key={item.tempId} className="align-top">
											<td className="px-4 py-3">
												<div className="space-y-1">
													<div className="text-[10px] font-bold uppercase leading-none text-primary">
														{item.bloz07}
													</div>
													<div className="text-sm font-medium leading-tight">{item.name}</div>
												</div>
											</td>
											<td className="px-3 py-3">
												<Input
													type="number"
													step="1"
													min="0"
													placeholder="0"
													value={item.mslDostepnosc === 0 ? "" : item.mslDostepnosc}
													onChange={(e) =>
														updateMslItem(item.tempId, {
															mslDostepnosc: Number.parseInt(e.target.value, 10) || 0,
														})
													}
												/>
											</td>
											{mslWymagalnoscTryb === "per_produkt" && (
												<td className="px-3 py-3">
													<Input
														type="number"
														step="0.01"
														min="0"
														max="100"
														placeholder="np. 80.00"
														value={item.procentWymagalnosci ?? ""}
														onChange={(e) =>
															updateMslItem(item.tempId, {
																procentWymagalnosci: parseOptionalNumber(e.target.value),
															})
														}
													/>
												</td>
											)}
											<td className="px-3 py-3 text-right">
												<button
													type="button"
													className="text-muted-foreground transition-colors hover:text-status-error-text max-md:inline-flex max-md:size-9 max-md:items-center max-md:justify-center"
													onClick={() => removeMslItem(item.tempId)}
												>
													<X className="size-4" />
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				<div className="space-y-4">
					<div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-2">
						<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
							Brandy
						</h3>
						<div className="flex items-center gap-2 max-md:flex-wrap">
							<div className="flex items-center">
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-9 rounded-r-none border-r-0"
									onClick={() => document.getElementById("brand-template-import")?.click()}
								>
									<FileUp className="mr-2 size-4" />
									Importuj brandy
									<input
										id="brand-template-import"
										type="file"
										className="hidden"
										accept=".xls,.xlsx"
										onChange={handleBrandImport}
									/>
								</Button>
								<TooltipProvider>
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<div className="flex h-9 items-center justify-center rounded-r-md border border-input bg-background px-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-help transition-colors">
												<Info className="size-4" />
											</div>
										</TooltipTrigger>
										<TooltipContent className="max-w-xs space-y-2 p-3 text-xs" side="bottom">
											<p className="font-bold">Instrukcja importu brandów:</p>
											<ul className="list-disc pl-4 space-y-1">
												<li>
													Akceptowane pliki: <span className="font-mono">.xlsx, .xls</span>
												</li>
												<li>
													Wymagane kolumny:
													<ol className="list-decimal pl-4 mt-1">
														<li>
															<span className="font-bold text-primary">Nazwa</span>
														</li>
														<li>
															<span className="font-bold text-primary">Minimum</span> - minimalna
															wartość planu zakupu ex-factory
														</li>
														<li>
															<span className="font-bold text-primary">Wartość 100%</span>
														</li>
														<li>kolumny progów, np. 100%, 110%, 115%</li>
													</ol>
												</li>
												<li>
													W komórce progu wpisz tylko jedną wartość:
													<span className="font-mono"> 2,5%</span> albo
													<span className="font-mono"> 3000 zł</span>.
												</li>
												<li>Nie mieszaj w jednej komórce procentu i zł.</li>
												<li>
													Skorzystaj z przycisku
													<span className="font-mono"> Pobierz przykład</span>, jeśli chcesz zacząć
													od gotowego pliku.
												</li>
											</ul>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="h-9"
								onClick={downloadBrandImportTemplate}
							>
								<Download className="mr-2 size-4" />
								Pobierz przykład
							</Button>
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="h-9 gap-2"
								onClick={exportBrands}
								disabled={brands.length === 0}
							>
								<Download className="size-4" />
								Eksport
							</Button>
							<Button type="button" variant="outline" size="sm" className="h-9" onClick={addBrand}>
								<Plus className="mr-2 size-4" />
								Dodaj brand
							</Button>
						</div>
					</div>

					{brands.length === 0 ? (
						<div className="rounded-lg border border-dashed border-border bg-muted/30 py-8 text-center">
							<p className="text-sm text-muted-foreground">Brak zdefiniowanych brandów.</p>
						</div>
					) : (
						<div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm max-md:overflow-x-auto">
							<table className="w-full border-collapse text-xs max-md:min-w-[44rem]">
								<thead>
									<tr className="border-b border-border bg-muted/40">
										<th className="px-4 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Nazwa brandu
										</th>
										<th className="w-56 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Wartość 100% zakupu ex-factory
										</th>
										<th className="w-56 px-3 py-2 text-left font-bold uppercase tracking-tighter text-muted-foreground">
											Minimalna wartość planu zakupu ex-factory
										</th>
										<th className="w-12 px-3 py-2" />
									</tr>
								</thead>
								<tbody className="divide-y divide-border/40">
									{brands.map((brand) => (
										<tr key={brand.tempId}>
											<td className="px-4 py-3 align-top">
												<div className="space-y-3">
													<Input
														value={brand.name}
														onChange={(e) => updateBrand(brand.tempId, { name: e.target.value })}
														placeholder="np. Betaloc"
													/>
													<div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
														<div className="flex items-center justify-between">
															<div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
																Progi rozliczenia brandu
															</div>
															<Button
																type="button"
																variant="outline"
																size="sm"
																className="h-7 text-[10px] max-md:h-9"
																onClick={() => addBrandSettlementTier(brand.tempId)}
															>
																<Plus className="mr-1 size-3" />
																Dodaj próg
															</Button>
														</div>
														{(brand.settlementTiers ?? []).length === 0 ? (
															<div className="rounded border border-dashed border-border bg-background py-3 text-center text-[11px] text-muted-foreground">
																Brak progów rozliczenia.
															</div>
														) : (
															<div className="space-y-2">
																<div className="hidden items-center gap-2 px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground md:grid md:grid-cols-[120px_1fr_1fr_32px]">
																	<span>Od progu (%)</span>
																	<span>Rozliczenie (%)</span>
																	<span>Rozliczenie (zł)</span>
																	<span />
																</div>
																{brand.settlementTiers
																	.slice()
																	.sort((a, b) => a.thresholdPercent - b.thresholdPercent)
																	.map((tier) => (
																		<div
																			key={tier.tempId}
																			className="grid grid-cols-1 gap-2 rounded border border-border bg-background p-2 md:grid-cols-[120px_1fr_1fr_32px]"
																		>
																			<Input
																				type="number"
																				min={0}
																				step="0.01"
																				value={tier.thresholdPercent}
																				onChange={(e) =>
																					updateBrandSettlementTier(brand.tempId, tier.tempId, {
																						thresholdPercent:
																							parseOptionalNumber(e.target.value) ?? 0,
																					})
																				}
																				placeholder="Od progu (%)"
																			/>
																			<Input
																				type="number"
																				min={0}
																				step="0.01"
																				value={tier.settlementPercent ?? ""}
																				onChange={(e) =>
																					updateBrandSettlementTier(brand.tempId, tier.tempId, {
																						settlementPercent: parseOptionalNumber(e.target.value),
																					})
																				}
																			/>
																			<Input
																				type="number"
																				min={0}
																				step="0.01"
																				value={tier.settlementAmount ?? ""}
																				onChange={(e) =>
																					updateBrandSettlementTier(brand.tempId, tier.tempId, {
																						settlementAmount: parseOptionalNumber(e.target.value),
																					})
																				}
																			/>
																			<button
																				type="button"
																				className="text-muted-foreground transition-colors hover:text-status-error-text max-md:inline-flex max-md:size-9 max-md:items-center max-md:justify-center"
																				onClick={() =>
																					removeBrandSettlementTier(brand.tempId, tier.tempId)
																				}
																			>
																				<X className="size-4" />
																			</button>
																		</div>
																	))}
															</div>
														)}
													</div>
												</div>
											</td>
											<td className="px-3 py-3 align-top">
												<Input
													type="number"
													min={0}
													step="0.01"
													value={brand.purchaseValue100Exfactory ?? ""}
													onChange={(e) =>
														updateBrand(brand.tempId, {
															purchaseValue100Exfactory: parseOptionalNumber(e.target.value) ?? 0,
														})
													}
													placeholder="0.00"
												/>
											</td>
											<td className="px-3 py-3 align-top">
												<Input
													type="number"
													min={0}
													step="0.01"
													value={brand.minPurchaseExfactoryValue ?? ""}
													onChange={(e) =>
														updateBrand(brand.tempId, {
															minPurchaseExfactoryValue: parseOptionalNumber(e.target.value) ?? 0,
														})
													}
													placeholder="0.00"
												/>
											</td>
											<td className="px-3 py-3 text-right align-top">
												<button
													type="button"
													className="text-muted-foreground transition-colors hover:text-status-error-text max-md:inline-flex max-md:size-9 max-md:items-center max-md:justify-center"
													onClick={() => removeBrand(brand.tempId)}
												>
													<X className="size-4" />
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{/* ─── KOSZYKI PRODUKTÓW ─── */}
				<div className="space-y-4">
					<div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-2">
						<h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
							Koszyki produktów
						</h3>
						<div className="flex items-center gap-2 max-md:flex-wrap">
							<div className="flex items-center">
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="gap-2 relative rounded-r-none border-r-0 h-9"
									onClick={() => document.getElementById("basket-template-import")?.click()}
								>
									<FileUp className="size-4" />
									Importuj koszyki z szablonu
									<input
										id="basket-template-import"
										type="file"
										className="hidden"
										accept=".xls,.xlsx"
										onChange={handleBasketImport}
									/>
								</Button>
								<TooltipProvider>
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<div className="flex h-9 items-center justify-center rounded-r-md border border-input bg-background px-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-help transition-colors">
												<Info className="size-4" />
											</div>
										</TooltipTrigger>
										<TooltipContent className="max-w-xs space-y-2 p-3 text-xs" side="bottom">
											<p className="font-bold">Instrukcja importu koszyków:</p>
											<ul className="list-disc pl-4 space-y-1">
												<li>
													Akceptowane pliki: <span className="font-mono">.xlsx, .xls</span>
												</li>
												<li>
													Wymagane kolumny w kolejności:
													<ol className="list-decimal pl-4 mt-1">
														<li>
															<span className="font-bold">BLOZ</span> (7 cyfr)
														</li>
														<li>Nazwa produktu (może być pusta)</li>
														<li>Cena ex-factory (może być pusta)</li>
														<li>
															<span className="font-bold text-primary">Nazwa koszyka</span>
														</li>
														<li>Brand (opcjonalnie, nazwa zgodna z sekcją Brandy)</li>
													</ol>
												</li>
												<li>Produkty zostaną pogrupowane w koszyki według nazwy w 4. kolumnie.</li>
												<li>
													Jeżeli w 5. kolumnie podasz brand, system spróbuje automatycznie przypisać
													go do produktu po nazwie brandu.
												</li>
											</ul>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="gap-2 h-9"
								onClick={exportBasketProducts}
							>
								<Download className="size-4" />
								Eksport produktów
							</Button>

							<Button type="button" variant="outline" size="sm" onClick={addBasket} className="h-9">
								<Plus className="size-4 mr-2" />
								Dodaj koszyk
							</Button>
						</div>
					</div>

					{baskets.length === 0 && (
						<div className="text-center py-8 bg-muted/30 rounded-lg border border-dashed border-border">
							<p className="text-sm text-muted-foreground">Brak zdefiniowanych koszyków.</p>
						</div>
					)}

					<div className="space-y-6">
						{baskets.map((basket, bIdx) => (
							<div
								key={basket.tempId}
								className="bg-card rounded-lg border border-border shadow-sm overflow-hidden"
							>
								{/* Nagłówek koszyka */}
								<div className="bg-muted/30 p-4 border-b border-border flex items-center justify-between max-md:gap-2 max-md:p-3">
									<div className="flex-1 max-w-md space-y-1">
										<label
											htmlFor={`${formId}-basket-name-${bIdx}`}
											className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider"
										>
											Nazwa koszyka
										</label>
										<Input
											id={`${formId}-basket-name-${bIdx}`}
											required
											value={basket.name}
											onChange={(e) => updateBasket(bIdx, { name: e.target.value })}
											placeholder="np. Kosz OTC"
											className="h-9 bg-background"
										/>
									</div>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="text-muted-foreground hover:text-status-error-text"
										onClick={() => removeBasket(bIdx)}
									>
										<Trash2 className="size-4" />
									</Button>
								</div>

								<div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-border">
									{/* Lewa kolumna: Produkty */}
									<div className="lg:col-span-4 p-4 space-y-4 max-md:p-3">
										<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
											<h4 className="text-xs font-bold uppercase text-muted-foreground tracking-tighter">
												Produkty ({basket.products.length})
											</h4>
											<div className="flex items-center gap-1.5 max-md:[&_button]:h-9">
												{basket.products.length > 0 && (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														className="h-7 text-[10px] text-status-error-text hover:bg-status-error/10"
														onClick={() => {
															const query = (basketProductSearches[basket.tempId] ?? "")
																.trim()
																.toLowerCase();
															const visibleCount = query
																? basket.products.filter(
																		(product) =>
																			product.bloz07.toLowerCase().includes(query) ||
																			(product.name?.toLowerCase().includes(query) ?? false),
																	).length
																: basket.products.length;
															setClearProductsTarget({
																basketIndex: bIdx,
																name: basket.name,
																count: basket.products.length,
																visibleCount,
															});
														}}
													>
														<Trash2 className="size-3 mr-1" /> Usuń wszystkie
													</Button>
												)}
												<Button
													type="button"
													variant="outline"
													size="sm"
													className="h-7 text-[10px]"
													onClick={() => {
														setProductSelectionTarget({ kind: "basket", basketIndex: bIdx });
														setIsProductModalOpen(true);
													}}
												>
													<Plus className="size-3 mr-1" /> Dodaj produkty
												</Button>
											</div>
										</div>

										{basket.products.length > 5 && (
											<div className="relative">
												<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground" />
												<Input
													placeholder="Szukaj w koszyku..."
													value={basketProductSearches[basket.tempId] ?? ""}
													onChange={(e) =>
														setBasketProductSearches((prev) => ({
															...prev,
															[basket.tempId]: e.target.value,
														}))
													}
													className="h-7 pl-8 text-[10px] bg-muted/20 max-md:h-9 max-md:text-sm"
												/>
											</div>
										)}

										{(() => {
											const rawQuery = basketProductSearches[basket.tempId] ?? "";
											const q = rawQuery.trim().toLowerCase();
											const filtered = q
												? basket.products.filter(
														(p) =>
															p.bloz07.toLowerCase().includes(q) ||
															(p.name?.toLowerCase().includes(q) ?? false),
													)
												: basket.products;
											return (
												<div className="space-y-2 max-h-[400px] overflow-auto pr-1">
													{filtered.map((p) => (
														<div
															key={p.bloz07}
															className="flex items-start gap-3 p-2 bg-background border border-border rounded-md group hover:border-primary/30 transition-colors"
														>
															<div className="flex-1 min-w-0">
																<div className="text-[10px] font-bold text-primary opacity-80 uppercase leading-none mb-1">
																	{p.bloz07}
																</div>
																<div className="text-sm font-medium truncate tracking-tight leading-tight">
																	{p.name}
																</div>
																<select
																	className="mt-2 flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 max-md:h-9 max-md:text-sm"
																	value={p.brandClientId ?? ""}
																	onChange={(e) =>
																		updateBasketProductBrand(
																			bIdx,
																			p.bloz07,
																			e.target.value || undefined,
																		)
																	}
																>
																	<option value="">Bez brandu</option>
																	{getBrandOptionsForBasket(bIdx).map((brand) => (
																		<option key={brand.tempId} value={brand.tempId}>
																			{brand.name || "Brand bez nazwy"}
																		</option>
																	))}
																</select>
															</div>
															<button
																type="button"
																className="text-muted-foreground hover:text-status-error-text opacity-0 group-hover:opacity-100 transition-opacity max-md:-m-2 max-md:p-2.5 max-md:opacity-100"
																onClick={() => removeProductFromBasket(bIdx, p.bloz07)}
															>
																<X className="size-4" />
															</button>
														</div>
													))}
													{basket.products.length === 0 && (
														<div className="text-center py-6 text-xs text-muted-foreground bg-muted/20 rounded border border-dashed border-border">
															Brak produktów
														</div>
													)}
													{basket.products.length > 0 && filtered.length === 0 && (
														<div className="text-center py-6 text-xs text-muted-foreground bg-muted/20 rounded border border-dashed border-border">
															Brak wyników dla frazy "{rawQuery}"
														</div>
													)}
												</div>
											);
										})()}
									</div>

									{/* Prawa kolumna: Rozliczenia */}
									<div className="lg:col-span-8 p-4 space-y-4 bg-muted/5 max-md:p-3">
										<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
											<h4 className="text-xs font-bold uppercase text-muted-foreground tracking-tighter">
												Rozliczenia ({basket.settlements.length}/4)
											</h4>
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="h-7 text-[10px] max-md:h-9"
												disabled={basket.settlements.length >= 4}
												onClick={() => addSettlement(bIdx)}
											>
												<Plus className="size-3 mr-1" /> Dodaj rozliczenie
											</Button>
										</div>

										<div className="grid grid-cols-1 divide-y divide-border">
											{basket.settlements.map((s, sIdx) => (
												<div
													key={s.tempId}
													className="py-4 first:pt-0 last:pb-0 space-y-4 relative group"
												>
													<button
														type="button"
														className="absolute top-0 right-0 text-muted-foreground hover:text-status-error-text max-md:-mt-2.5 max-md:-mr-2.5 max-md:p-2.5"
														onClick={() => removeSettlement(bIdx, sIdx)}
													>
														<X className="size-4" />
													</button>

													<div className="space-y-4">
														<div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-md:grid-cols-1 max-md:gap-3 max-md:pr-8">
															<div className="space-y-1">
																<label
																	htmlFor={`${formId}-s-start-${bIdx}-${sIdx}`}
																	className="text-[9px] font-bold uppercase text-muted-foreground"
																>
																	Od
																</label>
																<Input
																	id={`${formId}-s-start-${bIdx}-${sIdx}`}
																	type="date"
																	className="h-8 text-xs p-1 max-md:h-9"
																	value={s.startDate}
																	onChange={(e) =>
																		updateSettlement(bIdx, sIdx, {
																			startDate: e.target.value,
																		})
																	}
																/>
															</div>
															<div className="space-y-1">
																<label
																	htmlFor={`${formId}-s-end-${bIdx}-${sIdx}`}
																	className="text-[9px] font-bold uppercase text-muted-foreground"
																>
																	Do
																</label>
																<Input
																	id={`${formId}-s-end-${bIdx}-${sIdx}`}
																	type="date"
																	className="h-8 text-xs p-1 max-md:h-9"
																	value={s.endDate}
																	onChange={(e) =>
																		updateSettlement(bIdx, sIdx, {
																			endDate: e.target.value,
																		})
																	}
																/>
															</div>
															<div className="space-y-1">
																<label
																	htmlFor={`${formId}-s-purchase-${bIdx}-${sIdx}`}
																	className="text-[9px] font-bold uppercase text-muted-foreground leading-tight"
																>
																	Wartość zakupu 100%
																</label>
																<Input
																	id={`${formId}-s-purchase-${bIdx}-${sIdx}`}
																	type="number"
																	step="0.01"
																	placeholder="0.00"
																	className="h-8 text-xs max-md:h-9"
																	value={s.totalPurchaseValue || ""}
																	onChange={(e) =>
																		updateSettlement(bIdx, sIdx, {
																			totalPurchaseValue: parseFloat(e.target.value),
																		})
																	}
																/>
															</div>
															<div className="space-y-1">
																<label
																	htmlFor={`${formId}-s-sales-${bIdx}-${sIdx}`}
																	className="text-[9px] font-bold uppercase text-muted-foreground leading-tight"
																>
																	Wartość sprzedaży 100%
																</label>
																<Input
																	id={`${formId}-s-sales-${bIdx}-${sIdx}`}
																	type="number"
																	step="0.01"
																	placeholder="0.00"
																	className="h-8 text-xs max-md:h-9"
																	value={s.totalSalesValue || ""}
																	onChange={(e) =>
																		updateSettlement(bIdx, sIdx, {
																			totalSalesValue: parseFloat(e.target.value),
																		})
																	}
																/>
															</div>
														</div>

														<div className="space-y-2">
															<button
																type="button"
																className="flex w-full items-center justify-between rounded-md border border-border/50 bg-background/40 px-3 py-2 text-left hover:border-primary/30 hover:bg-background/70 transition-colors"
																onClick={() => toggleSettlementComponents(s.tempId)}
															>
																<span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
																	Składniki rozliczenia
																</span>
																{expandedSettlementComponents[s.tempId] ? (
																	<ChevronUp className="size-3.5 text-muted-foreground" />
																) : (
																	<ChevronDown className="size-3.5 text-muted-foreground" />
																)}
															</button>
															{expandedSettlementComponents[s.tempId] && (
																<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
																	{settlementComponentFields.map((field) => (
																		<div key={field.key} className="space-y-1">
																			<label
																				htmlFor={`${formId}-s-${field.key}-${bIdx}-${sIdx}`}
																				className="text-[9px] font-bold uppercase text-muted-foreground leading-tight"
																			>
																				{field.label}
																			</label>
																			<Input
																				id={`${formId}-s-${field.key}-${bIdx}-${sIdx}`}
																				type="number"
																				step="0.01"
																				placeholder="0.00"
																				className="h-8 text-xs max-md:h-9"
																				value={s[field.key] ?? ""}
																				onChange={(e) =>
																					updateSettlement(bIdx, sIdx, {
																						[field.key]: parseOptionalNumber(e.target.value),
																					} as Partial<SettlementWithTempId>)
																				}
																			/>
																		</div>
																	))}
																</div>
															)}
														</div>
													</div>

													<div className="space-y-2 bg-background/50 p-3 rounded-md border border-border/50">
														<div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
															<span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">
																Progi (Zakup + Sprzedaż)
															</span>
															<Button
																type="button"
																variant="ghost"
																className="h-6 text-[9px] px-2 font-bold hover:bg-primary/10 hover:text-primary max-md:h-9 max-md:text-xs"
																onClick={() => addTier(bIdx, sIdx)}
															>
																<Plus className="size-3 mr-1" /> Dodaj wiersz progu
															</Button>
														</div>

														<div className="space-y-2">
															{/* Nagłówki dla progów */}
															{s.tiers.length > 0 && (
																<div className="grid grid-cols-[2fr_3fr_1.4fr_2fr_3fr_1.4fr_auto] gap-2 px-1 max-md:hidden">
																	<div className="col-span-3 text-[8px] font-bold uppercase text-orange-500/80 text-center border-b border-orange-500/20 pb-1">
																		Zakup
																	</div>
																	<div className="col-span-3 text-[8px] font-bold uppercase text-blue-500/80 text-center border-b border-blue-500/20 pb-1">
																		Sprzedaż
																	</div>
																	<div />
																</div>
															)}

															{s.tiers.map((t, tIdx) => (
																<div
																	key={t.tempId}
																	className="grid grid-cols-[2fr_3fr_1.4fr_2fr_3fr_1.4fr_auto] gap-2 items-end bg-background p-2 rounded border border-border/40 hover:border-primary/20 transition-colors shadow-sm max-md:grid-cols-3"
																>
																	<div className="space-y-1">
																		<div className="flex items-center justify-between">
																			<label
																				htmlFor={`tier-purchase-from-${t.tempId}`}
																				className="text-[8px] font-bold opacity-50 uppercase text-orange-600/70"
																			>
																				Zak. Od
																			</label>
																			<span className="text-[8px] font-bold text-orange-600">
																				{calculatePercent(t.purchaseFrom, s.totalPurchaseValue)}
																			</span>
																		</div>
																		<Input
																			id={`tier-purchase-from-${t.tempId}`}
																			type="number"
																			step="0.01"
																			className="h-7 text-[10px] p-1 max-md:h-9 max-md:text-sm"
																			value={Number.isFinite(t.purchaseFrom) ? t.purchaseFrom : ""}
																			onChange={(e) =>
																				updateTier(bIdx, sIdx, tIdx, {
																					purchaseFrom: parseFloat(e.target.value),
																				})
																			}
																		/>
																	</div>
																	<div className="space-y-1">
																		<div className="flex items-center justify-between">
																			<label
																				htmlFor={`tier-purchase-to-${t.tempId}`}
																				className="text-[8px] font-bold opacity-50 uppercase text-orange-600/70"
																			>
																				Zak. Do
																			</label>
																			{t.purchaseTo && (
																				<span className="text-[8px] font-bold text-orange-600">
																					{calculatePercent(t.purchaseTo, s.totalPurchaseValue)}
																				</span>
																			)}
																		</div>
																		<Input
																			id={`tier-purchase-to-${t.tempId}`}
																			type="number"
																			step="0.01"
																			className="h-7 text-[10px] p-1 max-md:h-9 max-md:text-sm"
																			value={t.purchaseTo || ""}
																			placeholder="∞"
																			onChange={(e) =>
																				updateTier(bIdx, sIdx, tIdx, {
																					purchaseTo: parseOptionalNumber(e.target.value),
																				})
																			}
																		/>
																	</div>

																	<div className="space-y-1">
																		<label
																			htmlFor={`tier-purchase-percent-${t.tempId}`}
																			className="text-[8px] font-bold opacity-50 uppercase text-orange-600/70"
																		>
																			Zak. %
																		</label>
																		<Input
																			id={`tier-purchase-percent-${t.tempId}`}
																			type="number"
																			step="0.01"
																			className="h-7 text-[10px] p-1 font-bold text-orange-600 max-md:h-9 max-md:text-sm"
																			value={
																				Number.isFinite(t.purchasePercent) ? t.purchasePercent : ""
																			}
																			onChange={(e) =>
																				updateTier(bIdx, sIdx, tIdx, {
																					purchasePercent: parseFloat(e.target.value),
																				})
																			}
																		/>
																	</div>

																	<div className="space-y-1">
																		<div className="flex items-center justify-between">
																			<label
																				htmlFor={`tier-sales-from-${t.tempId}`}
																				className="text-[8px] font-bold opacity-50 uppercase"
																			>
																				Sprz. Od
																			</label>
																			<span className="text-[8px] font-bold text-blue-600">
																				{calculatePercent(t.salesFrom, s.totalSalesValue)}
																			</span>
																		</div>
																		<Input
																			id={`tier-sales-from-${t.tempId}`}
																			type="number"
																			step="0.01"
																			className="h-7 text-[10px] p-1 max-md:h-9 max-md:text-sm"
																			value={Number.isFinite(t.salesFrom) ? t.salesFrom : ""}
																			onChange={(e) =>
																				updateTier(bIdx, sIdx, tIdx, {
																					salesFrom: parseFloat(e.target.value),
																				})
																			}
																		/>
																	</div>
																	<div className="space-y-1">
																		<div className="flex items-center justify-between">
																			<label
																				htmlFor={`tier-sales-to-${t.tempId}`}
																				className="text-[8px] font-bold opacity-50 uppercase"
																			>
																				Sprz. Do
																			</label>
																			{t.salesTo && (
																				<span className="text-[8px] font-bold text-blue-600">
																					{calculatePercent(t.salesTo, s.totalSalesValue)}
																				</span>
																			)}
																		</div>
																		<Input
																			id={`tier-sales-to-${t.tempId}`}
																			type="number"
																			step="0.01"
																			className="h-7 text-[10px] p-1 max-md:h-9 max-md:text-sm"
																			value={t.salesTo || ""}
																			placeholder="∞"
																			onChange={(e) =>
																				updateTier(bIdx, sIdx, tIdx, {
																					salesTo: parseOptionalNumber(e.target.value),
																				})
																			}
																		/>
																	</div>

																	<div className="space-y-1">
																		<label
																			htmlFor={`tier-sales-percent-${t.tempId}`}
																			className="text-[8px] font-bold opacity-50 uppercase text-blue-600/70"
																		>
																			Sprz. %
																		</label>
																		<Input
																			id={`tier-sales-percent-${t.tempId}`}
																			type="number"
																			step="0.01"
																			className="h-7 text-[10px] p-1 font-bold text-blue-600 max-md:h-9 max-md:text-sm"
																			value={Number.isFinite(t.salesPercent) ? t.salesPercent : ""}
																			onChange={(e) =>
																				updateTier(bIdx, sIdx, tIdx, {
																					salesPercent: parseFloat(e.target.value),
																				})
																			}
																		/>
																	</div>
																	<div className="flex justify-end pb-1 max-md:col-start-3 max-md:pb-0">
																		<button
																			type="button"
																			className="text-muted-foreground hover:text-status-error-text transition-colors max-md:inline-flex max-md:size-9 max-md:items-center max-md:justify-center"
																			onClick={() => removeTier(bIdx, sIdx, tIdx)}
																		>
																			<Trash2 className="size-3.5" />
																		</button>
																	</div>
																</div>
															))}
														</div>
													</div>
												</div>
											))}
											{basket.settlements.length === 0 && (
												<div className="text-center py-10 text-xs text-muted-foreground bg-background border border-dashed border-border rounded-lg">
													Brak zdefiniowanych rozliczeń. Kliknij "Dodaj rozliczenie".
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</form>

			<div
				className={`fixed bottom-0 right-0 left-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm transition-[left] duration-200 md:left-16 max-md:sticky max-md:-mx-3 ${isPanelOpen ? "md:left-72" : ""}`}
			>
				<div className="mx-auto flex max-w-[1600px] justify-end gap-3 px-6 py-3 max-md:px-3 max-md:py-2 max-md:[&>button]:h-11 max-md:[&>button]:flex-1">
					<Button type="button" variant="outline" onClick={() => navigate({ to: "/kontrakty" })}>
						Anuluj
					</Button>
					<Button type="submit" form={formId} disabled={saveMutation.isPending}>
						{isEdit ? "Zapisz zmiany" : "Utwórz kontrakt"}
					</Button>
				</div>
			</div>

			<ProductSelectorModal
				open={isProductModalOpen}
				onOpenChange={(open) => {
					setIsProductModalOpen(open);
					if (!open) {
						setProductSelectionTarget(null);
					}
				}}
				onSelect={handleProductsSelect}
				title={
					productSelectionTarget?.kind === "surcharge"
						? "Dodaj produkty do dopłat"
						: productSelectionTarget?.kind === "msl"
							? "Dodaj produkty do MSL"
							: "Dodaj produkty"
				}
				initialSelection={
					productSelectionTarget?.kind === "basket"
						? (baskets[productSelectionTarget.basketIndex]?.products.map(
								(product) => product.bloz07,
							) ?? [])
						: productSelectionTarget?.kind === "msl"
							? mslItems.map((item) => item.bloz07)
							: productSurcharges.map((item) => item.bloz07)
				}
			/>

			<PharmacySelectorModal
				open={isPharmacyModalOpen}
				onOpenChange={setIsPharmacyModalOpen}
				onSelect={handlePharmaciesSelect}
				initialSelection={pharmacies.map((p) => p.unitcode)}
			/>

			<ConfirmDialog
				open={clearProductsTarget !== null}
				onOpenChange={(open) => {
					if (!open) setClearProductsTarget(null);
				}}
				title="Usunąć wszystkie produkty z koszyka?"
				description={
					clearProductsTarget
						? `Z koszyka „${clearProductsTarget.name || "bez nazwy"}" zniknie wszystkie ${
								clearProductsTarget.count
							} pozycji${
								clearProductsTarget.visibleCount < clearProductsTarget.count
									? ` — także te ukryte przez wyszukiwarkę (widocznych teraz: ${clearProductsTarget.visibleCount})`
									: ""
							}. Pozostałe koszyki i rozliczenia zostają bez zmian, a zmiana wchodzi po zapisaniu kontraktu.`
						: undefined
				}
				variant="destructive"
				confirmLabel="Usuń wszystkie"
				onConfirm={() => {
					if (clearProductsTarget) clearBasketProducts(clearProductsTarget.basketIndex);
				}}
			/>
		</div>
	);
}
